/**
 * Core streaming functionality for AI providers
 */
import * as Sentry from "@sentry/react";
import { toolRegistry as globalToolRegistry } from "@/lib/ai/tools";
import { API_BASE_URL } from "@/lib/constants/app";
import useThreadStore from "@/store/threadStore";

// Import backend provider services
import { BackendProviderService } from "@/lib/ai-providers/backend-provider-service";
import BackendProviderWrapper from "@/lib/ai-providers/backend-provider-wrapper";

// Import refactored utilities (removing abort and citations imports)
import { getEnabledToolNames } from "../../../ai/tools/toolValidation";
import { saveToolCallMessage } from "./databaseOperations";
import { processStreamDelta } from "./deltaProcessing";
import { createFollowUpParams } from "./followUpParamsHandler";
import {
  formatFollowUpMessages,
  handleFollowUpStreamCreationError,
  handleFollowUpStreamError,
  processFollowUpChunk,
} from "./followUpStreamProcessing";
import { ensureProviderMessageCompatibility } from "./providerCompatibility";
import { isReasoningModel } from "./reasoningModels";
import {
  flushBatchedUpdates,
  generateUUID,
  safelyUpdateStreamingMessage,
} from "./streamUtils";
import { buildStructuredSystemPrompt } from "./systemPromptBuilder";
import { safelyUpdateThreadState } from "./threadStateManagement";
import {
  getErrorType,
  getUserFriendlyErrorMessage,
  isRetryableError,
} from "@/components/Chat/messages/utils/errorTypeMapper";
import {
  cleanToolArguments,
  finalizeToolCallArguments,
  formatMessagesForVision,
  handleToolCalls,
} from "./toolHandling";
import {
  addReasoningEffortIfNeeded,
  configureToolParams,
  getBooleanValue,
  getDefaultChatParams,
  getProviderForModel,
  getProviderInitOptions,
  messagesNeedVisionFormatting,
  needsVisionFormatting,
  processPdfFiles,
  removeUnsupportedParams,
} from "./utils";

import { useAssistantStore } from "@/store";
import useAuthStore from "@/store/authStore";
import memoryService from "@/services/memoryService";
import { canvasEventEmitter } from "../../../ai/tools/canvasStreamingTool.js";

// Client-side rate limiting for auto-analysis
// This prevents excessive API calls while the backend also enforces rate limits
const ANALYSIS_COOLDOWN_MS = 15000; // 15 seconds (reduced from 60s for better UX)
const analysisLastRun = new Map(); // userId -> timestamp
const firstAnalysisRun = new Set(); // threadId set - tracks which threads have had first analysis

/**
 * Check if auto-analysis is allowed (client-side rate limit)
 * First analysis in a thread is always allowed (no cooldown)
 * Subsequent analyses require cooldown to have passed
 * @param {string} userId - User ID
 * @param {string} threadId - Thread ID (optional, for first-analysis bypass)
 * @returns {boolean} - Whether analysis is allowed
 */
function canRunAutoAnalysis(userId, threadId = null) {
  if (!userId) return false;

  // Always allow first analysis in a thread (no cooldown)
  if (threadId && !firstAnalysisRun.has(threadId)) {
    return true;
  }

  // After first analysis, apply cooldown
  const lastRun = analysisLastRun.get(userId) || 0;
  return Date.now() - lastRun >= ANALYSIS_COOLDOWN_MS;
}

/**
 * Record that auto-analysis was run for a user/thread
 * @param {string} userId - User ID
 * @param {string} threadId - Thread ID (optional)
 */
function recordAutoAnalysis(userId, threadId = null) {
  if (userId) {
    analysisLastRun.set(userId, Date.now());
  }
  if (threadId) {
    firstAnalysisRun.add(threadId);
  }
}

/**
 * Clean message data before database save by removing non-schema fields
 * @param {Object} messageData - Raw message data
 * @returns {Object} - Cleaned message data safe for database
 */
function cleanMessageForDatabase(messageData) {
  const cleaned = { ...messageData };

  // Remove fields that don't exist in the database schema
  delete cleaned.toolCalls; // camelCase version doesn't exist in schema
  delete cleaned.isStreaming;
  delete cleaned.isStreamComplete;
  delete cleaned.messageStatus;
  delete cleaned.skipDatabaseSave;
  delete cleaned._dependencies;
  delete cleaned._immediate;
  delete cleaned.threadId; // Use thread_id instead

  // Ensure required fields are properly set
  cleaned.message_id = cleaned.message_id || cleaned.id;

  // Ensure tool_calls is properly formatted as string if it exists
  if (cleaned.tool_calls && typeof cleaned.tool_calls !== "string") {
    cleaned.tool_calls = JSON.stringify(cleaned.tool_calls);
  }

  return cleaned;
}

// Initialize backend provider service
let backendProviderService = null;

// Configuration flag to use backend providers
const USE_BACKEND_PROVIDERS = true;

/**
 * Initialize backend provider service if needed
 */
async function initializeBackendService() {
  if (!backendProviderService && USE_BACKEND_PROVIDERS) {
    try {
      backendProviderService = new BackendProviderService();
      // Backend provider service initialized
    } catch (error) {
      // Failed to initialize backend provider service
    }
  }
}

/**
 * Create a provider wrapper for the specified provider
 * @param {string} providerName - Name of the provider
 * @param {Object} options - Provider options
 * @returns {BackendProviderWrapper} - Provider wrapper instance
 */
function createProviderWrapper(providerName, options = {}) {
  return new BackendProviderWrapper(providerName, options);
}

/**
 * Process images using universal vision API for all providers
 * @param {Array} messages - Message history
 * @param {Object} contextData - Context data containing image information
 * @returns {Promise<Array>} - Enhanced messages with vision analysis
 */
async function processUniversalVision(messages, contextData) {
  // Check if we have images to process
  const hasImages =
    contextData?.has_images === true ||
    contextData?.has_images === "true" ||
    (contextData?.vision_image_urls &&
      contextData.vision_image_urls.length > 0) ||
    (contextData?.image_count && parseInt(contextData.image_count) > 0);

  if (!hasImages) {
    return messages;
  }

  // Extract image URLs from various sources
  let imageUrls = [];

  // From contextData.vision_image_urls
  if (Array.isArray(contextData.vision_image_urls)) {
    imageUrls.push(...contextData.vision_image_urls);
  }

  // From storage_files in contextData
  try {
    if (contextData.storage_files) {
      let storageFiles =
        typeof contextData.storage_files === "string"
          ? JSON.parse(contextData.storage_files)
          : contextData.storage_files;

      if (Array.isArray(storageFiles)) {
        const fileUrls = storageFiles
          .filter(
            (file) =>
              (file.isImage || file.type?.startsWith("image/")) &&
              (file.visionMetadata?.publicUrl || file.publicUrl || file.url)
          )
          .map(
            (file) =>
              file.visionMetadata?.publicUrl || file.publicUrl || file.url
          );
        imageUrls.push(...fileUrls);
      }
    }
  } catch (e) {
    // Error parsing storage_files for images
  }

  // Remove duplicates and filter out falsy values
  const uniqueImageUrls = [...new Set(imageUrls)].filter(Boolean);

  if (uniqueImageUrls.length === 0) {
    return messages;
  }

  try {
    // Get the last user message content for the question
    const lastUserMessage = messages.filter((msg) => msg.role === "user").pop();

    // Extract text content from various message formats
    let question = "What do you see in these images?";
    if (lastUserMessage?.content) {
      if (typeof lastUserMessage.content === "string") {
        question = lastUserMessage.content;
      } else if (Array.isArray(lastUserMessage.content)) {
        // Handle OpenAI vision format where content is an array
        const textContent = lastUserMessage.content.find(
          (item) => item.type === "text"
        );
        if (textContent?.text) {
          question = textContent.text;
        }
      } else if (lastUserMessage.content.text) {
        question = lastUserMessage.content.text;
      }
    }

    // Call the vision-rag/ask API
    const apiUrl = `${API_BASE_URL}/api/ai/rag/vision/ask`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question:
          typeof question === "string"
            ? question
            : question?.text || "What do you see in these images?",
        urls: uniqueImageUrls,
        threshold: 0.3,
        limit: 10,
      }),
    });

    if (!response.ok) {
      return messages;
    }

    const visionResult = await response.json();

    if (!visionResult.success || !visionResult.answer) {
      return messages;
    }

    // Enhance the last user message with vision analysis wrapped in image XML tags
    const enhancedMessages = messages.map((message, index) => {
      if (index === messages.length - 1 && message.role === "user") {
        // Extract original content based on format
        let originalContent = "";
        if (typeof message.content === "string") {
          originalContent = message.content;
        } else if (Array.isArray(message.content)) {
          // Handle OpenAI vision format - extract text content
          const textContent = message.content.find(
            (item) => item.type === "text"
          );
          originalContent = textContent?.text || "";
        } else if (message.content?.text) {
          originalContent = message.content.text;
        } else {
          originalContent = message.content || "";
        }

        // Create vision content from relevantImages if available, otherwise use addedImages or main answer
        let visionContent = "";

        // Always include the main answer in a vision_result tag
        let visionResultContent = `<vision_result>\n${visionResult.answer}\n</vision_result>`;

        // Add individual image descriptions if available
        let imageDescriptions = "";
        if (
          visionResult.relevantImages &&
          visionResult.relevantImages.length > 0
        ) {
          // Use relevantImages first as they contain the full descriptions
          imageDescriptions = visionResult.relevantImages
            .map((image) => `<image>\n${image.description}\n</image>`)
            .join("\n\n");
        } else if (
          visionResult.processing?.addedImages &&
          visionResult.processing.addedImages.length > 0
        ) {
          // Fallback to addedImages if no relevantImages
          imageDescriptions = visionResult.processing.addedImages
            .map((image) => `<image>\n${image.description}\n</image>`)
            .join("\n\n");
        }

        // Combine vision result and image descriptions
        if (imageDescriptions) {
          visionContent = `${visionResultContent}\n\n${imageDescriptions}`;
        } else {
          visionContent = visionResultContent;
        }

        // Return message with enhanced content as string (universal format)
        return {
          ...message,
          content: `${originalContent}\n\n${visionContent}`,
          metadata: {
            ...message.metadata,
            has_vision_analysis: true,
            vision_processed_urls: uniqueImageUrls,
            vision_analysis_timestamp: new Date().toISOString(),
            vision_images_count: uniqueImageUrls.length,
            vision_relevant_images: visionResult.relevantImages?.length || 0,
            vision_added_images:
              visionResult.processing?.addedImages?.length || 0,
          },
        };
      }
      return message;
    });

    return enhancedMessages;
  } catch (error) {
    return messages;
  }
}

/**
 * Process a streaming chat completion with a selected provider
 * @param {Object} params - Parameters for streaming
 * @param {string} params.providerName - Name of the AI provider to use
 * @param {string} params.apiKey - API key for the provider
 * @param {string} params.modelName - Model name to use
 * @param {Array} params.messages - Message history
 * @param {string} params.instructions - System instructions
 * @param {Array} params.tools - Function tools to include
 * @param {Object} params.threadStore - Thread store instance or setState function
 * @param {Function} params.updateMessageState - Function to update message state
 * @param {Object} params.contextData - Additional context data
 * @param {string} params.messageId - ID for the message being streamed
 * @param {Object} params.tokenCounter - Token counter for tracking usage
 * @param {Function} params.navigate - Navigation function
 * @param {string} params.threadId - Thread ID
 * @param {string} params.workspace_id - Workspace ID
 * @param {Object} params.toolRegistry - Tool registry instance
 * @param {Function} params.saveToDatabase - Function to save messages to database
 * @param {string} params.userId - User ID for token usage recording
 * @param {Object} params.threadIdRef - Ref containing the locked thread ID to prevent race conditions
 */
export async function streamProviderChat({
  providerName = "openai",
  apiKey,
  modelName,
  messages,
  instructions,
  tools,
  threadStore,
  updateMessageState,
  contextData = {},
  messageId,
  tokenCounter,
  navigate,
  threadId,
  workspace_id,
  toolRegistry = globalToolRegistry,
  saveToDatabase = null,
  userId = null,
  threadIdRef = null,
  threadIdForStream = null, // BACKGROUND STREAMING: Thread ID for stream registration
  messageIdForStream = null, // BACKGROUND STREAMING: Message ID for stream registration
}) {
  // Import vector store utilities
  const { processVectorStoreRetrieval } = await import("./vectorStoreUtils");

  // RACE CONDITION FIX: Use threadIdRef if available to prevent race conditions
  const lockedThreadId = threadIdRef?.current || threadId;

  let enhancedInstructions = instructions || "";
  let retrievedFileIds = [];

  enhancedInstructions = await buildStructuredSystemPrompt(
    instructions,
    contextData
  );

  // Process vector store retrieval using refactored utilities

  const vectorStoreResult = await processVectorStoreRetrieval({
    contextData,
    threadId: lockedThreadId,
    messages,
  });

  // Update variables with results from vector store processing
  messages = vectorStoreResult.enhancedMessages;
  retrievedFileIds = vectorStoreResult.retrievedFileIds;
  contextData = vectorStoreResult.contextData;

  // Process universal vision for all providers
  messages = await processUniversalVision(messages, contextData);

  // Update contextData to indicate vision analysis has been applied
  const hasVisionAnalysis = messages.some(
    (msg) =>
      msg.metadata?.has_vision_analysis === true ||
      (typeof msg.content === "string" && msg.content.includes("<image>"))
  );

  if (hasVisionAnalysis) {
    contextData.has_vision_analysis = true;
  }

  // Removed abort handling functionality

  if (
    !toolRegistry ||
    !toolRegistry.executeTool ||
    typeof toolRegistry.executeTool !== "function"
  ) {
    toolRegistry = globalToolRegistry;
  }

  if (!messages || !Array.isArray(messages) || messages.length < 1) {
    throw new Error("No valid message history provided");
  }

  if (providerName === "anthropic") {
    messages = messages.map((message) => {
      if (message.role === "tool" && !message.tool_call_id) {
        return {
          ...message,
          tool_call_id: `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        };
      }
      return message;
    });
  }

  const provider = getProviderForModel(modelName);

  const actualProviderName = provider || providerName || "openai";

  // Initialize backend service if configured
  await initializeBackendService();

  let aiProvider;

  if (USE_BACKEND_PROVIDERS && backendProviderService) {
    // Use backend service - no need to create provider instance
    aiProvider = {
      isBackendService: true,
      providerName: actualProviderName,
      modelName: modelName,
    };
  } else {
    // Initialize backend service
    await initializeBackendService();

    // Create provider wrapper for backend service
    aiProvider = createProviderWrapper(actualProviderName, {
      defaultModel: modelName,
      ...getProviderInitOptions(actualProviderName),
    });

    // Removed abort handler setup
  }

  // Handle compatibility mode web search - execute search and append results to user message
  let compatibilityModeUsed = false;
  if (contextData?.forceWebSearchTool === true) {
    try {
      const { handleCompatibilityModeWebSearch } = await import(
        "./webSearchCompatibility"
      );
      const compatibilityResult = await handleCompatibilityModeWebSearch(
        modelName,
        messages,
        contextData
      );

      if (compatibilityResult.usedCompatibilityMode) {
        messages = compatibilityResult.messages;
        compatibilityModeUsed = true;
        // Store flag in contextData for system prompt builder and tool configuration
        contextData.webSearchCompatibilityMode = true;
        // Clear tool_choice to prevent API errors (tool not in tools array)
        contextData.clearToolChoice = true;
        console.log(
          "[StreamProviderChat] Compatibility mode web search executed for",
          modelName,
          "- tool calls stripped from messages"
        );
      }
    } catch (error) {
      console.error(
        "[StreamProviderChat] Error handling compatibility mode web search:",
        error
      );
      // Continue with normal flow if compatibility mode fails
    }
  }

  let chatParams = getDefaultChatParams(
    modelName,
    ensureProviderMessageCompatibility(messages, actualProviderName, modelName),
    actualProviderName,
    contextData
  );

  if (actualProviderName === "openai") {
    const { hasPdfFiles, pdfFileIds, pdfFiles } = processPdfFiles(contextData);

    chatParams.messages = formatMessagesForVision(chatParams.messages, {
      ...contextData,
      has_pdfs: hasPdfFiles,
      pdf_files: pdfFiles,
      pdf_file_ids: pdfFileIds,
    });
  }

  chatParams = await addReasoningEffortIfNeeded(
    chatParams,
    modelName,
    actualProviderName,
    contextData,
    threadStore?.getState?.()?.currentAssistant || {}
  );

  chatParams = removeUnsupportedParams(
    chatParams,
    modelName,
    actualProviderName
  );

  if (actualProviderName === "anthropic") {
    if (enhancedInstructions) {
      const hasSystemMessage = chatParams.messages.some(
        (m) => m.role === "system"
      );
      if (!hasSystemMessage) {
        chatParams.messages.unshift({
          role: "system",
          content: enhancedInstructions,
        });
      }
    }
  } else {
    if (enhancedInstructions) {
      const hasSystemMessage = chatParams.messages.some(
        (m) => m.role === "system"
      );
      if (!hasSystemMessage) {
        chatParams.messages.unshift({
          role: "system",
          content: enhancedInstructions,
        });
      }
    }
  }
  chatParams = await configureToolParams(
    chatParams,
    tools,
    modelName,
    actualProviderName,
    contextData,
    useAssistantStore.getState().currentAssistant
  );

  // Extract enabled tool names for validation (use Set for O(1) lookup)
  const enabledToolNames = getEnabledToolNames(chatParams.tools);
  console.log(
    "[StreamProviderChat] Enabled tools for this request:",
    Array.from(enabledToolNames)
  );

  // Clear tool_choice if compatibility mode was used (prevents API errors)
  // Delete it entirely rather than setting to "none" to ensure backend doesn't process it
  if (compatibilityModeUsed || contextData?.clearToolChoice) {
    delete chatParams.tool_choice;
    console.log(
      "[StreamProviderChat] Deleted tool_choice due to compatibility mode"
    );
  }
  // Handle forced tool choice for image editing
  else if (contextData?.forceImageEditTool === true) {
    chatParams.tool_choice = {
      type: "function",
      function: { name: "image_edit" },
    };
  }
  // Handle forced tool choice for web search - CHECK FIRST to ensure user-selected tools take priority
  // Skip if compatibility mode was used (tool already executed and results added to context)
  else if (contextData?.forceWebSearchTool === true && !compatibilityModeUsed) {
    chatParams.tool_choice = {
      type: "function",
      function: { name: "web_search" },
    };
  }
  // Handle forced tool choice for image generation - also takes priority over canvas
  else if (contextData?.forceImageGenTool === true) {
    chatParams.tool_choice = {
      type: "function",
      function: { name: "image_gen" },
    };
  }
  // Handle forced tool choice for canvas creation - only if web search/image gen not selected
  else if (contextData?.forceCanvasCreate === true) {
    chatParams.tool_choice = {
      type: "function",
      function: { name: "canvas_create" },
    };
  }

  // STRICT TOOL CONTROL: Only allow tools if explicitly selected
  if (!contextData?.hasExplicitToolSelection) {
    // No tool explicitly selected - remove all tools and set tool_choice to "none"
    delete chatParams.tools;
    chatParams.tool_choice = "none";
    console.log(
      "[StreamProviderChat] Strict tool control: No explicit tool selection, removing all tools and setting tool_choice to 'none'"
    );
  }

  // Final check: If compatibility mode was used, ensure tool_choice is completely removed
  // This prevents any possibility of it being sent to the backend
  if (
    compatibilityModeUsed ||
    contextData?.webSearchCompatibilityMode === true ||
    contextData?.clearToolChoice === true
  ) {
    // Explicitly set to undefined (not just delete) to ensure it's not in the object
    chatParams.tool_choice = undefined;
    delete chatParams.tool_choice;
    // Also ensure tools array doesn't contain web_search
    if (chatParams.tools && Array.isArray(chatParams.tools)) {
      chatParams.tools = chatParams.tools.filter(
        (tool) =>
          !(tool.type === "function" && tool.function?.name === "web_search")
      );
      // If no tools left, remove the tools array entirely
      if (chatParams.tools.length === 0) {
        delete chatParams.tools;
        chatParams.tools = undefined;
      }
    }
    // Set a flag to tell backend to not process tool_choice
    chatParams.skipToolChoice = true;
    contextData.skipToolChoice = true;
    console.log(
      "[StreamProviderChat] Final cleanup: Removed tool_choice and web_search tool for compatibility mode",
      {
        hasToolChoice: "tool_choice" in chatParams,
        toolChoiceValue: chatParams.tool_choice,
        hasTools: !!chatParams.tools,
        toolsCount: chatParams.tools?.length || 0,
      }
    );
  }

  // Prepare tool selection context for debugging (stored in message metadata)
  const toolSelectionContext = {
    forceWebSearchTool: contextData?.forceWebSearchTool || false,
    forceImageGenTool: contextData?.forceImageGenTool || false,
    forceCanvasCreate: contextData?.forceCanvasCreate || false,
    forceImageEditTool: contextData?.forceImageEditTool || false,
    hasExplicitToolSelection: contextData?.hasExplicitToolSelection || false,
    tool_choice: chatParams?.tool_choice || "none",
  };

  // Debug logging for tool selection context
  if (import.meta.env.DEV) {
    console.log("[StreamProviderChat] Tool selection context:", {
      toolSelectionContext,
      contextData: {
        forceWebSearchTool: contextData?.forceWebSearchTool,
        forceImageGenTool: contextData?.forceImageGenTool,
        forceCanvasCreate: contextData?.forceCanvasCreate,
        forceImageEditTool: contextData?.forceImageEditTool,
        hasExplicitToolSelection: contextData?.hasExplicitToolSelection,
      },
      chatParamsToolChoice: chatParams?.tool_choice,
      showSystemPromptDebug:
        import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG === "true",
    });
  }

  let modelProvider;
  let currentMessageId = messageId;
  let messageBuffer = "";
  let hiddenBuffer = "";
  let bufferingForToolCall = false;
  let followUpBuffer = "";
  let toolResults = [];
  let followUpMessageSaved = false;
  let savedFollowUpMessageId = null;
  let reasoningContent = "";
  let reasoningMetadata = {};
  let reasoningDuration = 0;
  let reasoningStartTime = null; // Track when reasoning starts (declared at function scope)
  let inReasoningBlock = false;
  let isReasoningResponse = false;
  let streamCompleted = false;
  let toolCallsFullyAccumulated = false;
  let hasDetectedToolCall = false;
  let validToolCalls = [];
  let pendingToolCalls = [];

  // ADDED: Declare followUpContentId at function scope so it's accessible throughout
  let followUpContentId = null;

  try {
    try {
      // If threadStore is actually setState function, we need to get the store differently
      let currentState;
      if (typeof threadStore === "function" && !threadStore.getState) {
        // threadStore is actually setState, get state from the store directly
        currentState = useThreadStore.getState();
      } else if (typeof threadStore?.getState === "function") {
        currentState = threadStore.getState();
      } else {
        currentState = {};
      }

      const existingMessages = currentState.messages || [];

      const setThreadState =
        typeof threadStore === "function" ? threadStore : threadStore.setState;

      setThreadState((state) => {
        const currentMessages = state.messages || [];

        // CRITICAL FIX: Preserve message order and ensure user messages come before assistant messages
        const messagesToKeep =
          currentMessages.length >= existingMessages.length
            ? currentMessages
            : existingMessages;

        // Sort messages to ensure proper chronological order
        const sortedMessages = [...messagesToKeep].sort((a, b) => {
          const dateA = new Date(a.created_at || a.timestamp || Date.now());
          const dateB = new Date(b.created_at || b.timestamp || Date.now());
          return dateA - dateB;
        });

        return {
          isStreaming: true,
          streamStatus: "started",
          messageStatus: "created",
          isStreamComplete: false,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: true,
          error: null,
          messages: sortedMessages,
          preventNewMessageClear: true,
          // CRITICAL FIX: Set currentMessageId in thread store for reasoning block detection
          currentMessageId: currentMessageId,
          // CRITICAL FIX: Track when streaming started to prevent premature fetches
          streamStartedAt: Date.now(),
          lastStreamActivity: Date.now(),
        };
      });

      const abortController = new AbortController();
      setThreadState((state) => ({
        ...state,
        abortController,
        preventNewMessageClear: true,
      }));

      // BACKGROUND STREAMING: Register stream in threadStore
      if (threadIdForStream && messageIdForStream) {
        const threadStoreState = useThreadStore.getState();
        const registered = threadStoreState.registerStream(
          threadIdForStream,
          messageIdForStream,
          abortController
        );
        if (!registered) {
          console.warn(
            `[StreamProviderChat] Failed to register stream for thread ${threadIdForStream}`
          );
        }
      }

      modelProvider = getProviderForModel(modelName) || providerName;

      // Create provider wrapper for backend service
      await initializeBackendService();

      const provider = createProviderWrapper(modelProvider, {
        defaultModel: modelName,
        ...(modelProvider === "anthropic" || modelProvider === "cohere"
          ? { dangerouslyAllowBrowser: true }
          : {}),
      });

      // Provider info check is handled by backend service
      const providers = [{ id: modelProvider, supportsStreaming: true }];
      const providerInfo = providers.find((p) => p.id === modelProvider);

      if (!providerInfo?.supportsStreaming) {
        throw new Error(`Provider ${modelProvider} does not support streaming`);
      }

      const formattedMessages = provider.formatMessageHistory(
        messages,
        enhancedInstructions
      );

      messageBuffer = "";
      currentMessageId = messageId;

      let params = {
        model: modelName,
        messages: formattedMessages,
        temperature: contextData.temperature || 0.2,
        max_tokens: contextData.max_tokens || 16000,
        stream: true,
      };

      params = await configureToolParams(
        params,
        tools,
        modelName,
        modelProvider,
        contextData,
        useAssistantStore.getState().currentAssistant
      );

      // Handle forced tool choice for image editing
      if (contextData?.forceImageEditTool === true) {
        params.tool_choice = {
          type: "function",
          function: { name: "image_edit" },
        };
      }

      // Handle forced tool choice for canvas creation
      if (contextData?.forceCanvasCreate === true) {
        params.tool_choice = {
          type: "function",
          function: { name: "canvas_create" },
        };
      }

      // Handle forced tool choice for web search
      // CRITICAL: Check web search BEFORE canvas to ensure user-selected tools take priority
      if (contextData?.forceWebSearchTool === true) {
        // Web search takes priority over canvas mode when explicitly selected
        params.tool_choice = {
          type: "function",
          function: { name: "web_search" },
        };
      }

      // Handle forced tool choice for image generation
      if (contextData?.forceImageGenTool === true) {
        // Image generation also takes priority over canvas when explicitly selected
        params.tool_choice = {
          type: "function",
          function: { name: "image_gen" },
        };
      }

      params = await addReasoningEffortIfNeeded(
        params,
        modelName,
        modelProvider,
        contextData,
        threadStore?.getState?.()?.currentAssistant || {}
      );

      params = removeUnsupportedParams(params, modelName, modelProvider);

      // Fix thread store access for preserving user messages
      if (threadStore) {
        try {
          let currentState;
          if (typeof threadStore === "function" && !threadStore.getState) {
            // threadStore is actually setState, get state from the store directly
            currentState = useThreadStore.getState();
          } else if (typeof threadStore?.getState === "function") {
            currentState = threadStore.getState();
          } else {
            currentState = {};
          }

          const currentMessages = currentState.messages || [];

          if (currentMessages.length > 0) {
            const lastUserMessage = [...currentMessages]
              .reverse()
              .find((msg) => msg.role === "user");

            if (lastUserMessage) {
              const setStoreState =
                typeof threadStore === "function"
                  ? threadStore
                  : threadStore.setState;

              setStoreState((state) => ({
                ...state,
                preventNewMessageClear: true,
                pendingUserMessage: lastUserMessage,
                pendingUserMessageId: lastUserMessage.id,
                messages: state.messages || currentMessages,
              }));
            }
          }
        } catch (preserveError) {
          // Ignore errors when preserving user messages
        }
      }

      // Reset variables for this streaming session
      hasDetectedToolCall = false;
      validToolCalls = [];
      pendingToolCalls = [];
      followUpBuffer = "";
      followUpMessageSaved = false;
      savedFollowUpMessageId = null;
      toolResults = [];
      reasoningContent = "";
      reasoningMetadata = {};
      reasoningDuration = 0;
      reasoningStartTime = null; // Track when reasoning starts
      inReasoningBlock = false;
      isReasoningResponse = false;
      streamCompleted = false;
      toolCallsFullyAccumulated = false;
      followUpContentId = null;

      // generateUUID is now imported from streamUtils

      const functionResponseUUID = generateUUID();

      // Removed citations handling

      const functionResponseMessageId = functionResponseUUID;

      if (threadStore) {
        const setStoreState =
          typeof threadStore === "function"
            ? threadStore
            : threadStore.setState;

        setStoreState({
          functionResponseMessageId,
          lastFunctionResponseMessageId: functionResponseMessageId,
        });
      }

      if (needsVisionFormatting(actualProviderName, contextData)) {
        if (messagesNeedVisionFormatting(params.messages)) {
          params.messages = formatMessagesForVision(
            params.messages,
            contextData
          );
        }
      }

      if (
        provider.ensureVisionFormat &&
        (getBooleanValue(contextData.has_vision_enabled) ||
          getBooleanValue(contextData.has_images) ||
          (contextData.vision_image_urls &&
            contextData.vision_image_urls.length > 0) ||
          getBooleanValue(contextData.has_pdfs) ||
          (contextData.pdf_file_ids && contextData.pdf_file_ids.length > 0))
      ) {
        const visionParams = {
          visionData: {
            has_vision_enabled: contextData.has_vision_enabled,
            has_images: contextData.has_images,
            image_count: contextData.image_count,
            vision_image_urls: contextData.vision_image_urls,
            has_pdfs: contextData.has_pdfs,
            pdf_count: contextData.pdf_count,
            pdf_file_ids: contextData.pdf_file_ids,
          },
          contextData: contextData,
        };

        params.messages = provider.ensureVisionFormat(
          params.messages,
          visionParams
        );
      }

      if (modelProvider === "anthropic") {
        if (params.messages && Array.isArray(params.messages)) {
          params.messages = params.messages.map((message) => {
            if (message.role === "tool") {
              if (!message.tool || !message.tool.tool_call_id) {
                const toolCallId =
                  message.tool_call_id ||
                  `call_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

                return {
                  role: "tool",
                  tool: {
                    tool_call_id: toolCallId,
                  },
                  content: message.content || "",
                };
              }
            }
            return message;
          });
        }
      }

      if (needsVisionFormatting(actualProviderName, contextData)) {
        if (messagesNeedVisionFormatting(params.messages)) {
          params.messages = formatMessagesForVision(
            params.messages,
            contextData
          );
        }
      }

      // Choose streaming method based on configuration
      if (USE_BACKEND_PROVIDERS && backendProviderService) {
        // For backend providers, we need to ensure proper message formatting
        // Get a temporary provider instance just for message formatting
        let messageFormatter = null;
        try {
          await initializeBackendService();
          messageFormatter = createProviderWrapper(actualProviderName, {
            defaultModel: modelName,
          });
        } catch (error) {
          // Ignore message formatter creation errors
        }

        // Format messages properly for the backend - this will validate and fix tool call sequences
        const formattedMessages = messageFormatter
          ? messageFormatter.formatMessageHistory(
              params.messages,
              enhancedInstructions
            )
          : params.messages;

        // Log system prompt for troubleshooting
        const systemMessage = formattedMessages.find(
          (msg) => msg.role === "system"
        );
        if (systemMessage) {
          console.log("🔍 [System Prompt Debug]", {
            provider: actualProviderName,
            model: modelName,
            tools: params.tools?.map((t) => t.function?.name || t.name) || [],
            tool_choice: params.tool_choice,
            systemPromptLength: systemMessage.content?.length || 0,
            systemPrompt: systemMessage.content,
          });
        }

        // DEBUG: Log the exact prompt (all messages) being sent to the model
        console.log("📤 [DEBUG][PROMPT] Full Prompt Being Sent to Model:", {
          provider: actualProviderName,
          model: modelName,
          threadId: threadIdForStream,
          messageCount: formattedMessages.length,
          temperature: params.temperature,
          max_tokens: params.max_tokens,
          tools: params.tools?.map((t) => t.function?.name || t.name) || [],
          tool_choice: params.tool_choice,
          fullMessages: formattedMessages.map((msg, idx) => ({
            index: idx,
            role: msg.role,
            content:
              typeof msg.content === "string"
                ? msg.content
                : Array.isArray(msg.content)
                  ? msg.content.map((c) => ({
                      type: c.type,
                      text: c.type === "text" ? c.text : "[non-text content]",
                      image_url:
                        c.type === "image_url" ? "[image data]" : undefined,
                    }))
                  : "[complex content]",
            tool_calls: msg.tool_calls
              ? `[${msg.tool_calls.length} tool call(s)]`
              : undefined,
            tool_call_id: msg.tool_call_id,
            name: msg.name,
          })),
        });

        const streamPromise = backendProviderService.createChatCompletionStream(
          {
            provider: actualProviderName,
            model: modelName,
            messages: formattedMessages, // Use the validated and fixed messages
            temperature: params.temperature,
            max_tokens: params.max_tokens,
            tools: params.tools,
            tool_choice: params.tool_choice,
            instructions: enhancedInstructions,
            threadId: threadIdForStream, // Include threadId for tracking
            contextData: contextData,
            ...params,
          },
          async (delta) => {
            try {
              // Log raw delta for debugging image_gen
              if (delta?.choices?.[0]?.delta?.tool_calls) {
                const toolCalls = delta.choices[0].delta.tool_calls || [];
                if (toolCalls.some((tc) => tc.function?.name === "image_gen")) {
                  // Raw delta detection intentionally silent to avoid console noise.
                }
              }

              // Process stream delta using utility function
              const deltaResult = processStreamDelta({
                delta,
                modelProvider: actualProviderName, // Use actual provider name
                modelName: delta.model || modelName, // ADDED: Use model from delta if available, fallback to original
                messageId: currentMessageId, // ADDED: Pass message ID for Grok buffering
                reasoningState: {
                  isReasoningResponse,
                  reasoningContent,
                  inReasoningBlock,
                  reasoningStartTime, // Pass the actual start time, not null
                  reasoningDuration,
                },
                pendingToolCalls,
                bufferingForToolCall,
                hasDetectedToolCall,
                toolCallsFullyAccumulated: 0,
                hasToolsEnabled: params.tools && params.tools.length > 0, // Only buffer Grok content when tools are enabled
              });

              // Log when tool calls are detected after processing
              // Update state from delta processing
              const deltaContent = deltaResult.deltaContent;

              // CRITICAL FIX: Merge reasoningMetadata instead of overwriting to preserve citations
              // Previously: reasoningMetadata = deltaResult.reasoningMetadata;
              // This was overwriting any previously captured citations
              if (deltaResult.reasoningMetadata) {
                reasoningMetadata = {
                  ...reasoningMetadata,
                  ...deltaResult.reasoningMetadata,
                };
              }

              // Capture citations from Perplexity responses
              if (
                deltaResult.citations &&
                Array.isArray(deltaResult.citations)
              ) {
                console.log(
                  "[StreamProviderChat:backend] Captured citations from delta:",
                  deltaResult.citations.length,
                  "sources"
                );
                // Store citations in metadata for later use
                if (!reasoningMetadata) {
                  reasoningMetadata = {};
                }
                reasoningMetadata.citations = deltaResult.citations;
                reasoningMetadata.hasCitations = true;
              }

              // Update reasoning state
              isReasoningResponse =
                deltaResult.reasoningState.isReasoningResponse;
              reasoningContent = deltaResult.reasoningState.reasoningContent;
              inReasoningBlock = deltaResult.reasoningState.inReasoningBlock;
              reasoningStartTime =
                deltaResult.reasoningState.reasoningStartTime; // Capture updated start time
              reasoningDuration = deltaResult.reasoningState.reasoningDuration;

              // Update tool call state
              hasDetectedToolCall = deltaResult.hasDetectedToolCall;
              bufferingForToolCall = deltaResult.bufferingForToolCall;
              pendingToolCalls = deltaResult.pendingToolCalls;
              toolCallsFullyAccumulated = deltaResult.toolCallsFullyAccumulated;
              streamCompleted = deltaResult.streamCompleted;

              // ADDED: Set currentToolCallName immediately when first tool call is detected
              // This enables the skeleton loader to show as soon as possible
              if (
                deltaResult.hasDetectedToolCall &&
                pendingToolCalls.length > 0 &&
                threadStore
              ) {
                const firstToolCall = pendingToolCalls[0];
                const toolName =
                  firstToolCall?.function?.name || firstToolCall?.name;
                if (toolName) {
                  const setThreadState =
                    typeof threadStore === "function"
                      ? threadStore
                      : threadStore.setState;
                  setThreadState({
                    currentToolCallName: toolName,
                    currentToolCallId: firstToolCall?.id,
                  });
                }
              }

              // Log when stream completes with image_gen tool calls (backend provider)
              if (
                streamCompleted &&
                deltaResult.streamCompleted &&
                pendingToolCalls.length > 0
              ) {
                const imageGenCalls = pendingToolCalls.filter(
                  (tc) => tc.function?.name === "image_gen"
                );
                // Image generation calls are handled elsewhere in the streaming pipeline
              }

              // ADDED: Handle Grok-specific buffering
              const isGrokBuffering =
                deltaResult.grokBuffering?.shouldBuffer &&
                deltaResult.grokBuffering?.isGrokProvider;
              const hasGrokReleasedContent =
                deltaResult.grokBuffering?.releasedContent &&
                deltaResult.grokBuffering.releasedContent.length > 0;

              // Handle stream completion for tool calls
              if (
                streamCompleted &&
                deltaResult.streamCompleted &&
                pendingToolCalls.length > 0
              ) {
                if (updateMessageState) {
                  updateMessageState({
                    id: currentMessageId,
                    role: "assistant", // Fixed: assistant messages make tool calls
                    tool_calls: pendingToolCalls,
                    isStreaming: false,
                    isStreamComplete: true,
                    skipDatabaseSave: true,
                    thread_id: lockedThreadId, // RACE CONDITION FIX: Include thread ID
                  });

                  if (threadStore) {
                    const setThreadState =
                      typeof threadStore === "function"
                        ? threadStore
                        : threadStore.setState;

                    setThreadState((state) => {
                      const currentMessages = state.messages || [];

                      const updatedMessages = currentMessages.map((msg) =>
                        msg.id === currentMessageId && msg.role === "assistant" // Fixed: check for assistant role
                          ? {
                              ...msg,
                              tool_calls: pendingToolCalls,
                              isStreaming: false,
                              isStreamComplete: true,
                            }
                          : msg
                      );

                      return {
                        ...state,
                        messages: updatedMessages,
                        toolCalls: pendingToolCalls,
                        preventNewMessageClear: true,
                      };
                    });
                  }
                }
              }

              if (tokenCounter) {
                tokenCounter.countCompletionTokens(deltaContent);
              }

              // Check if canvas mode is enabled or if we're calling a canvas tool
              // Suppress text content in either case
              const isCanvasModeEnabled =
                contextData?.isCanvasModeEnabled === true ||
                contextData?.forceCanvasCreate === true;
              const isCallingCanvasTool = pendingToolCalls.some((tc) => {
                const toolName = tc.function?.name || tc.name;
                return (
                  toolName === "canvas_create" ||
                  toolName === "canvas_edit" ||
                  toolName === "canvas_stream"
                );
              });
              const shouldSuppressText =
                isCanvasModeEnabled || isCallingCanvasTool;

              // Clear message buffer if canvas tool is detected (to remove any pre-generated text)
              if (isCallingCanvasTool && messageBuffer.length > 0) {
                messageBuffer = "";
              }

              // MODIFIED: Handle Grok buffering for message buffer
              if (bufferingForToolCall || isGrokBuffering) {
                // For Grok, we might not want to add preliminary content to hiddenBuffer
                if (!isGrokBuffering || deltaContent) {
                  hiddenBuffer += deltaContent;
                }
              } else if (!shouldSuppressText) {
                // Only accumulate text content if NOT in canvas mode or calling a canvas tool
                messageBuffer += deltaContent;
              }
              // else: Text is suppressed when canvas mode is active or canvas tool is called

              // CRITICAL FIX: Update UI for both main content AND reasoning content
              // Production build approach: Just check isReasoningResponse flag, not content existence
              const hasReasoningUpdate = isReasoningResponse;
              const shouldUpdate =
                (deltaContent || hasReasoningUpdate) &&
                !bufferingForToolCall &&
                !isGrokBuffering;

              if (updateMessageState && shouldUpdate) {
                safelyUpdateStreamingMessage(
                  updateMessageState,
                  currentMessageId,
                  messageBuffer,
                  "streaming",
                  true, // Use batching for better performance
                  // Include reasoning metadata during streaming
                  {
                    provider: actualProviderName,
                    model: modelName,
                    // Add toolSelectionContext and systemPrompt to streaming metadata
                    toolSelectionContext,
                    ...(import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG ===
                      "true" && enhancedInstructions
                      ? { systemPrompt: enhancedInstructions }
                      : {}),
                    // Production build approach: Include reasoning metadata when isReasoningResponse is true, regardless of content
                    ...(isReasoningResponse
                      ? {
                          reasoningContent: reasoningContent || "",
                          isReasoningResponse: true,
                          inReasoningBlock: inReasoningBlock,
                          reasoningDuration: reasoningDuration,
                        }
                      : {}),
                    ...(reasoningMetadata || {}),
                    // Ensure citations are included if present (for Perplexity)
                    ...(reasoningMetadata?.citations
                      ? {
                          citations: reasoningMetadata.citations,
                          hasCitations: true,
                        }
                      : {}),
                  },
                  lockedThreadId, // RACE CONDITION FIX: Pass locked thread ID
                  "assistant" // FIXED: Explicitly specify assistant role
                );

                if (
                  hasDetectedToolCall &&
                  pendingToolCalls.length > 0 &&
                  toolCallsFullyAccumulated
                ) {
                  // Flush batched updates before final tool call update
                  flushBatchedUpdates(updateMessageState);

                  updateMessageState({
                    id: currentMessageId,
                    role: "assistant", // Fixed: assistant messages make tool calls
                    tool_calls: pendingToolCalls,
                    skipDatabaseSave: true,
                    isStreaming: false,
                    isStreamComplete: true,
                    thread_id: lockedThreadId, // RACE CONDITION FIX: Include thread ID
                  });
                }
              }

              // Tool call processing is now handled in processStreamDelta

              // Anthropic-specific tool processing is now handled in processStreamDelta
            } catch (e) {
              console.error("Error processing stream delta:", e);
            }
          },
          {
            signal: abortController.signal, // CRITICAL FIX: Pass abort signal to backend
            threadId: lockedThreadId,
            toolRegistry: toolRegistry,
            updateMessageState,
            enhancedErrorHandling: true,
            visionData: {
              has_vision_enabled:
                contextData.has_vision_enabled === true ||
                contextData.has_vision_enabled === "true" ||
                contextData.has_images === true ||
                contextData.has_images === "true",
              has_images:
                contextData.has_images === true ||
                contextData.has_images === "true",
              vision_image_urls: contextData.vision_image_urls || [],
              image_count: parseInt(contextData.image_count) || 0,
              has_pdfs:
                contextData.has_pdfs === true ||
                contextData.has_pdfs === "true",
              pdf_files: contextData.pdf_files || [],
              pdf_file_ids: contextData.pdf_file_ids || [],
            },
            contextData,
          }
        );

        // After stream completes, check if we need to execute tools
        await streamPromise;

        // Ensure streamCompleted is set if we have pending tool calls
        if (
          !streamCompleted &&
          hasDetectedToolCall &&
          pendingToolCalls.length > 0
        ) {
          streamCompleted = true;
        }
      } else {
        // Ensure we have the backend service for direct API calls
        await initializeBackendService();

        const directProvider = createProviderWrapper(modelProvider, {
          defaultModel: modelName,
          ...(modelProvider === "anthropic" || modelProvider === "cohere"
            ? { dangerouslyAllowBrowser: true }
            : {}),
        });

        // Format messages for direct API calls as well
        const formattedParamsForDirect = {
          ...params,
          messages: directProvider.formatMessageHistory(
            params.messages,
            enhancedInstructions
          ),
        };

        await directProvider.createChatCompletionStream(
          formattedParamsForDirect,
          async (delta) => {
            try {
              // Process stream delta using utility function
              const deltaResult = processStreamDelta({
                delta,
                modelProvider,
                modelName: delta.model || modelName, // ADDED: Use model from delta if available, fallback to original
                messageId: currentMessageId, // ADDED: Pass message ID for Grok buffering
                reasoningState: {
                  isReasoningResponse,
                  reasoningContent,
                  inReasoningBlock,
                  reasoningStartTime, // Pass the actual start time, not null
                  reasoningDuration,
                },
                pendingToolCalls,
                bufferingForToolCall,
                hasDetectedToolCall,
                toolCallsFullyAccumulated: 0,
                hasToolsEnabled: params.tools && params.tools.length > 0, // Only buffer Grok content when tools are enabled
              });

              // Update state from delta processing
              const deltaContent = deltaResult.deltaContent;

              // CRITICAL FIX: Merge reasoningMetadata instead of overwriting to preserve citations
              if (deltaResult.reasoningMetadata) {
                reasoningMetadata = {
                  ...reasoningMetadata,
                  ...deltaResult.reasoningMetadata,
                };
              }

              // Capture citations from Perplexity responses
              if (
                deltaResult.citations &&
                Array.isArray(deltaResult.citations)
              ) {
                // Store citations in metadata for later use
                if (!reasoningMetadata) {
                  reasoningMetadata = {};
                }
                reasoningMetadata.citations = deltaResult.citations;
                reasoningMetadata.hasCitations = true;
              }

              // Update reasoning state
              isReasoningResponse =
                deltaResult.reasoningState.isReasoningResponse;
              reasoningContent = deltaResult.reasoningState.reasoningContent;
              inReasoningBlock = deltaResult.reasoningState.inReasoningBlock;
              reasoningStartTime =
                deltaResult.reasoningState.reasoningStartTime; // Capture updated start time
              reasoningDuration = deltaResult.reasoningState.reasoningDuration;

              // Update tool call state
              hasDetectedToolCall = deltaResult.hasDetectedToolCall;
              bufferingForToolCall = deltaResult.bufferingForToolCall;
              pendingToolCalls = deltaResult.pendingToolCalls;
              toolCallsFullyAccumulated = deltaResult.toolCallsFullyAccumulated;
              streamCompleted = deltaResult.streamCompleted;

              // ADDED: Set currentToolCallName immediately when first tool call is detected
              // This enables the skeleton loader to show as soon as possible
              if (
                deltaResult.hasDetectedToolCall &&
                pendingToolCalls.length > 0 &&
                threadStore
              ) {
                const firstToolCall = pendingToolCalls[0];
                const toolName =
                  firstToolCall?.function?.name || firstToolCall?.name;
                if (toolName) {
                  const setThreadState =
                    typeof threadStore === "function"
                      ? threadStore
                      : threadStore.setState;
                  setThreadState({
                    currentToolCallName: toolName,
                    currentToolCallId: firstToolCall?.id,
                  });
                }
              }

              // ADDED: Handle Grok-specific buffering
              const isGrokBuffering =
                deltaResult.grokBuffering?.shouldBuffer &&
                deltaResult.grokBuffering?.isGrokProvider;
              const hasGrokReleasedContent =
                deltaResult.grokBuffering?.releasedContent &&
                deltaResult.grokBuffering.releasedContent.length > 0;

              // Log Grok buffering activity

              // Handle stream completion for tool calls
              if (
                streamCompleted &&
                deltaResult.streamCompleted &&
                pendingToolCalls.length > 0
              ) {
                if (updateMessageState) {
                  updateMessageState({
                    id: currentMessageId,
                    role: "assistant", // Fixed: assistant messages make tool calls
                    tool_calls: pendingToolCalls,
                    isStreaming: false,
                    isStreamComplete: true,
                    skipDatabaseSave: true,
                    thread_id: lockedThreadId, // RACE CONDITION FIX: Include thread ID
                  });

                  if (threadStore) {
                    const setThreadState =
                      typeof threadStore === "function"
                        ? threadStore
                        : threadStore.setState;

                    setThreadState((state) => {
                      const currentMessages = state.messages || [];

                      const updatedMessages = currentMessages.map((msg) =>
                        msg.id === currentMessageId && msg.role === "assistant" // Fixed: check for assistant role
                          ? {
                              ...msg,
                              tool_calls: pendingToolCalls,
                              isStreaming: false,
                              isStreamComplete: true,
                            }
                          : msg
                      );

                      return {
                        ...state,
                        messages: updatedMessages,
                        toolCalls: pendingToolCalls,
                        preventNewMessageClear: true,
                      };
                    });
                  }
                }
              }

              if (tokenCounter) {
                tokenCounter.countCompletionTokens(deltaContent);
              }

              // Check if canvas mode is enabled or if we're calling a canvas tool
              // Suppress text content in either case
              const isCanvasModeEnabled =
                contextData?.isCanvasModeEnabled === true ||
                contextData?.forceCanvasCreate === true;
              const isCallingCanvasTool = pendingToolCalls.some((tc) => {
                const toolName = tc.function?.name || tc.name;
                return (
                  toolName === "canvas_create" ||
                  toolName === "canvas_edit" ||
                  toolName === "canvas_stream"
                );
              });
              const shouldSuppressText =
                isCanvasModeEnabled || isCallingCanvasTool;

              // Clear message buffer if canvas tool is detected (to remove any pre-generated text)
              if (isCallingCanvasTool && messageBuffer.length > 0) {
                console.log(
                  "🎨 [Stream] Clearing message buffer due to canvas tool call - removing pre-generated text",
                  { previousBufferLength: messageBuffer.length }
                );
                messageBuffer = "";
              }

              // MODIFIED: Handle Grok buffering for message buffer
              if (bufferingForToolCall || isGrokBuffering) {
                // For Grok, we might not want to add preliminary content to hiddenBuffer
                if (!isGrokBuffering || deltaContent) {
                  hiddenBuffer += deltaContent;
                }
              } else if (!shouldSuppressText) {
                // Only accumulate text content if NOT in canvas mode or calling a canvas tool
                messageBuffer += deltaContent;
              } else {
                console.log(
                  "🎨 [Stream] Suppressing text content - canvas mode active or canvas tool called"
                );
              }

              // CRITICAL FIX: Update UI for both main content AND reasoning content
              const hasReasoningUpdate =
                isReasoningResponse ||
                (reasoningContent && reasoningContent.trim() !== "");
              const shouldUpdate =
                (deltaContent || hasReasoningUpdate) &&
                !bufferingForToolCall &&
                !isGrokBuffering;

              if (updateMessageState && shouldUpdate) {
                safelyUpdateStreamingMessage(
                  updateMessageState,
                  currentMessageId,
                  messageBuffer,
                  "streaming",
                  true, // Use batching for better performance
                  // Include reasoning metadata during streaming
                  {
                    provider: actualProviderName,
                    model: modelName,
                    // Add toolSelectionContext and systemPrompt to streaming metadata
                    toolSelectionContext,
                    ...(import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG ===
                      "true" && enhancedInstructions
                      ? { systemPrompt: enhancedInstructions }
                      : {}),
                    ...(isReasoningResponse ||
                    (reasoningContent && reasoningContent.trim() !== "")
                      ? {
                          reasoningContent: reasoningContent.trim(),
                          isReasoningResponse: true,
                          inReasoningBlock: inReasoningBlock,
                          reasoningDuration: reasoningDuration,
                        }
                      : {}),
                    ...(reasoningMetadata || {}),
                    // Ensure citations are included if present (for Perplexity)
                    ...(reasoningMetadata?.citations
                      ? {
                          citations: reasoningMetadata.citations,
                          hasCitations: true,
                        }
                      : {}),
                  },
                  lockedThreadId, // RACE CONDITION FIX: Pass locked thread ID
                  "assistant" // FIXED: Explicitly specify assistant role
                );

                if (
                  hasDetectedToolCall &&
                  pendingToolCalls.length > 0 &&
                  toolCallsFullyAccumulated
                ) {
                  // Flush batched updates before final tool call update
                  flushBatchedUpdates(updateMessageState);

                  updateMessageState({
                    id: currentMessageId,
                    role: "assistant", // Fixed: assistant messages make tool calls
                    tool_calls: pendingToolCalls,
                    skipDatabaseSave: true,
                    isStreaming: false,
                    isStreamComplete: true,
                    thread_id: lockedThreadId, // RACE CONDITION FIX: Include thread ID
                  });
                }
              }

              // Tool call processing is now handled in processStreamDelta

              // Anthropic-specific tool processing is now handled in processStreamDelta
            } catch (e) {
              console.error("Error processing stream delta:", e);
            }
          },
          {
            signal: abortController.signal, // CRITICAL FIX: Pass abort signal to direct provider
            threadId: lockedThreadId,
            toolRegistry: toolRegistry,
            updateMessageState,
            enhancedErrorHandling: true,
            visionData: {
              has_vision_enabled:
                contextData.has_vision_enabled === true ||
                contextData.has_vision_enabled === "true" ||
                contextData.has_images === true ||
                contextData.has_images === "true",
              has_images:
                contextData.has_images === true ||
                contextData.has_images === "true",
              vision_image_urls: contextData.vision_image_urls || [],
              image_count: parseInt(contextData.image_count) || 0,
              has_pdfs:
                contextData.has_pdfs === true ||
                contextData.has_pdfs === "true",
              pdf_files: contextData.pdf_files || [],
              pdf_file_ids: contextData.pdf_file_ids || [],
            },
            contextData,
          }
        );
      }

      // Add debugging for Anthropic tool execution trigger
      if (modelProvider === "anthropic") {
        // Special handling for Anthropic: if we have tool calls but execution isn't triggered
        if (
          streamCompleted &&
          pendingToolCalls.length > 0 &&
          !hasDetectedToolCall
        ) {
          hasDetectedToolCall = true;
        }
      }

      // Log before checking tool execution condition
      if (
        streamCompleted &&
        hasDetectedToolCall &&
        pendingToolCalls.length > 0
      ) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 200));

          toolCallsFullyAccumulated = true;

          if (modelProvider === "anthropic" && pendingToolCalls.length > 0) {
            pendingToolCalls = finalizeToolCallArguments(
              pendingToolCalls,
              "anthropic"
            );
          }

          validToolCalls = [];
          for (let i = 0; i < pendingToolCalls.length; i++) {
            const toolCall = pendingToolCalls[i];

            if (modelProvider === "anthropic" && toolCall.function) {
              if (
                toolCall.function.arguments === "" ||
                toolCall.function.arguments === undefined ||
                toolCall.function.arguments === '""'
              ) {
                toolCall.function.arguments = "{}";
              }

              try {
                JSON.parse(toolCall.function.arguments);
              } catch (e) {
                toolCall.function.arguments = "{}";
              }
            }

            if (toolCall.isStreaming === true) {
              toolCall.isStreaming = false;
            }

            if (
              modelProvider === "anthropic" &&
              toolCall.function &&
              (toolCall.function.arguments === "" ||
                toolCall.function.arguments === undefined)
            ) {
              toolCall.function.arguments = "{}";
            }

            if (!toolCall.function?.name) {
              continue;
            }

            const args = toolCall.function.arguments || "{}";

            let parsedArgs;
            try {
              if (args === "" || args === undefined) {
                parsedArgs = {};
                validToolCalls.push(toolCall);
              } else {
                parsedArgs = JSON.parse(args);
                validToolCalls.push(toolCall);
              }
            } catch (e) {
              try {
                const cleanedArgs = cleanToolArguments(args, true);
                toolCall.function.arguments =
                  typeof cleanedArgs === "string"
                    ? cleanedArgs
                    : JSON.stringify(cleanedArgs);

                validToolCalls.push(toolCall);
              } catch (cleanError) {
                console.error("Error cleaning tool arguments:", cleanError);
              }
            }
          }

          if (threadStore) {
            const setStoreState =
              typeof threadStore === "function"
                ? threadStore
                : threadStore.setState;

            setStoreState({
              isExecutingTools: true,
              toolExecutionActive: true,
              currentToolCallIndex: 0,
            });
          }

          toolResults = [];

          for (let i = 0; i < validToolCalls.length; i++) {
            const toolCall = validToolCalls[i];

            try {
              if (threadStore) {
                const setStoreState =
                  typeof threadStore === "function"
                    ? threadStore
                    : threadStore.setState;

                setStoreState({
                  currentToolCallIndex: i,
                  currentToolCall: toolCall,
                  currentToolCallName: toolCall.function?.name,
                });
              }

              if (modelProvider === "anthropic" && toolCall.function) {
                if (
                  toolCall.function.arguments === "" ||
                  toolCall.function.arguments === undefined ||
                  toolCall.function.arguments === '""'
                ) {
                  toolCall.function.arguments = "{}";
                }
              }

              // Use standard tool handling for all tools
              try {
                let args = JSON.parse(toolCall.function.arguments || "{}");

                // Handle forced image edit tool with pre-populated data
                if (
                  toolCall.function.name === "image_edit" &&
                  contextData?.forceImageEditTool === true &&
                  contextData?.imageEditData
                ) {
                  // Merge the pre-populated data with any args from the tool call
                  args = {
                    ...contextData.imageEditData,
                    ...args, // Allow tool call args to override if needed
                  };
                }

                // Handle forced canvas create tool with pre-populated data
                if (
                  toolCall.function.name === "canvas_create" &&
                  contextData?.forceCanvasCreate === true &&
                  contextData?.canvasCreateData
                ) {
                  // Merge the pre-populated data with any args from the tool call
                  args = {
                    ...contextData.canvasCreateData,
                    ...args, // Allow tool call args to override if needed
                  };
                }

                // CRITICAL: Add sourceMessageId to canvas tools for save button functionality
                if (
                  toolCall.function.name === "canvas_create" ||
                  toolCall.function.name === "canvas_stream" ||
                  toolCall.function.name === "canvas_edit"
                ) {
                  args.sourceMessageId = currentMessageId;
                }

                if (
                  !toolRegistry ||
                  typeof toolRegistry.executeTool !== "function"
                ) {
                  throw new Error(
                    `Cannot execute tool ${toolCall.function.name}: toolRegistry.executeTool is not a function`
                  );
                }

                // Log before executing image_gen tool

                const result = await toolRegistry.executeTool(
                  toolCall.function.name,
                  enabledToolNames,
                  args
                );

                toolCall.result = result;

                toolResults.push({
                  toolCall,
                  result,
                  success: true,
                });

                const latestResult = toolResults[toolResults.length - 1].result;
                if (updateMessageState) {
                  // Use batching for tool result updates to prevent browser crashes during web search streaming
                  safelyUpdateStreamingMessage(
                    updateMessageState,
                    currentMessageId,
                    "", // No content change, just metadata update
                    "streaming",
                    true, // Force batching
                    {
                      ...contextData,
                      formattedOutput: latestResult,
                      toolOutput: latestResult,
                      toolOutputRefId: toolCall.id,
                      toolOutputRefName: toolCall.function.name,
                    },
                    lockedThreadId,
                    "assistant"
                  );
                }
              } catch (toolError) {
                // Check if this is a tool authorization error
                const isAuthError =
                  toolError.message?.includes("not authorized");

                const errorOutput = {
                  error: true,
                  message: toolError.message || "Tool execution failed",
                  details: String(toolError),
                  timestamp: new Date().toISOString(),
                  isAuthorizationError: isAuthError,
                };

                toolCall.result = errorOutput;
                toolCall.error = true;

                // Log authorization errors specifically
                if (isAuthError) {
                  console.error(
                    `[Tool Authorization Error] ${toolCall.function.name}:`,
                    toolError.message
                  );
                }

                toolResults.push({
                  toolCall,
                  result: errorOutput,
                  success: false,
                });

                if (updateMessageState) {
                  // Use batching for tool error updates to prevent browser crashes
                  safelyUpdateStreamingMessage(
                    updateMessageState,
                    currentMessageId,
                    "", // No content change, just metadata update
                    "streaming",
                    true, // Force batching
                    {
                      ...contextData,
                      formattedOutput: errorOutput,
                      toolOutput: errorOutput,
                      toolOutputRefId: toolCall.id,
                      toolOutputRefName: toolCall.function.name,
                      error: toolError.message || "Tool execution failed",
                    },
                    lockedThreadId,
                    "assistant"
                  );
                }
              }
            } catch (toolError) {
              const errorOutput = {
                error: true,
                message: toolError.message || "Tool execution failed",
                details: String(toolError),
                timestamp: new Date().toISOString(),
              };

              toolCall.result = errorOutput;
              toolCall.error = true;

              toolResults.push({
                toolCall,
                result: errorOutput,
                success: false,
              });

              if (updateMessageState) {
                // Use batching for tool error updates to prevent browser crashes
                safelyUpdateStreamingMessage(
                  updateMessageState,
                  currentMessageId,
                  "", // No content change, just metadata update
                  "streaming",
                  true, // Force batching
                  {
                    ...contextData,
                    formattedOutput: errorOutput,
                    toolOutput: errorOutput,
                    toolOutputRefId: toolCall.id,
                    toolOutputRefName: toolCall.function.name,
                    error: toolError.message || "Tool execution failed",
                  },
                  lockedThreadId,
                  "assistant"
                );
              }
            }
          }

          if (threadStore) {
            const setStoreState =
              typeof threadStore === "function"
                ? threadStore
                : threadStore.setState;

            const toolResultsMap = {};
            toolResults.forEach((result) => {
              if (result.toolCall && result.toolCall.id) {
                toolResultsMap[result.toolCall.id] = result.result;
              }
            });

            setStoreState({
              isExecutingTools: false,
              toolExecutionActive: false,
              isProcessingToolResults: true,
              toolResults,
              toolCalls: validToolCalls,
              toolResultsMap,
              // Reset tool call state after execution
              currentToolCallName: null,
              currentToolCallId: null,
              currentToolCallOutput: null,
            });

            if (typeof threadStore.getState === "function") {
              try {
                const threadState = threadStore.getState();
                const currentMessages = threadState.messages || [];

                // REMOVED: Tool messages are no longer added to thread store
                // Tool results are now only attached to the assistant message with tool_calls

                const assistantMessageIndex = currentMessages.findIndex(
                  (msg) =>
                    msg.id === currentMessageId && msg.role === "assistant"
                );

                const updatedAssistantMessage = {
                  id: currentMessageId,
                  role: "assistant",
                  content: messageBuffer || " ",
                  isStreaming: false,
                  isStreamComplete: true,
                  messageStatus: "completed",
                  tool_calls: validToolCalls,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  thread_id: lockedThreadId,
                  workspace_id: workspace_id,
                  skipDatabaseSave: false,
                };

                let updatedMessages;

                if (assistantMessageIndex !== -1) {
                  updatedMessages = [...currentMessages];
                  updatedMessages[assistantMessageIndex] =
                    updatedAssistantMessage;
                } else {
                  updatedMessages = [...currentMessages];

                  if (
                    !updatedMessages.some((msg) => msg.id === currentMessageId)
                  ) {
                    updatedMessages.push(updatedAssistantMessage);
                  }
                }

                // Sort messages to maintain chronological order
                updatedMessages.sort((a, b) => {
                  const dateA = new Date(
                    a.created_at || a.timestamp || Date.now()
                  );
                  const dateB = new Date(
                    b.created_at || b.timestamp || Date.now()
                  );
                  return dateA - dateB;
                });

                setStoreState((state) => ({
                  ...state,
                  messages: updatedMessages,
                  preventNewMessageClear: true,
                }));
              } catch (toolMsgError) {
                console.error("Error updating thread store:", toolMsgError);
              }
            }
          }

          if (threadStore) {
            const setStoreState =
              typeof threadStore === "function"
                ? threadStore
                : threadStore.setState;

            setStoreState({
              isExecutingTools: false,
              toolExecutionActive: false,
              isProcessingToolResults: true,
              toolResults,
            });
          }

          // Check if ALL tools failed - if so, skip follow-up stream to prevent verbose error analysis
          // Tool errors will be displayed inline via AIErrorDisplay component
          const allToolsFailed = toolResults.every(
            (result) =>
              result.success === false ||
              result.result?.error === true ||
              result.result?.isError === true
          );

          if (allToolsFailed) {
            console.log(
              "[streamProviderChat] All tools failed with errors - skipping follow-up stream"
            );

            // Update message state to complete without AI follow-up
            if (updateMessageState) {
              safelyUpdateStreamingMessage(
                updateMessageState,
                currentMessageId,
                "", // No content - errors will be displayed via tool results in UI
                "completed",
                false,
                {
                  ...contextData,
                  allToolsFailed: true,
                  skipFollowUp: true,
                },
                lockedThreadId,
                "assistant"
              );
            }

            // Reset store state
            if (threadStore) {
              const setStoreState =
                typeof threadStore === "function"
                  ? threadStore
                  : threadStore.setState;

              setStoreState({
                isExecutingTools: false,
                toolExecutionActive: false,
                isProcessingToolResults: false,
                isStreaming: false,
                currentToolCall: null,
                currentToolCallName: null,
                currentToolCallId: null,
                currentToolCallOutput: null,
              });
            }

            // Exit without follow-up stream
            return;
          }

          // formatFollowUpMessages is now imported from followUpStreamProcessing

          const customFormattedMessages = formatFollowUpMessages(
            toolResults,
            modelProvider,
            messages,
            enhancedInstructions,
            null, // Backend provider system doesn't need factory
            modelName
          );

          // Initialize followUpParams variable
          let followUpParams;

          if (modelProvider === "anthropic") {
            followUpParams = {
              model: modelName,
              messages: customFormattedMessages || followUpMessages,
              stream: true,
              max_tokens: 1500,
              temperature: 0.2,
              top_p: 1,
              ...(contextData.anthropic_version
                ? { anthropic_version: contextData.anthropic_version }
                : {}),
              ...(contextData.anthropic_beta
                ? { anthropic_beta: contextData.anthropic_beta }
                : {}),
            };

            if (tools && tools.length > 0) {
              followUpParams.tools = tools;

              followUpParams.anthropic_beta = "mc-beta-2";
            }
          }

          const toolMessages = customFormattedMessages
            ? customFormattedMessages.filter((m) => m.role === "tool")
            : toolResults.map(({ toolCall, result }) => {
                let resultContent;

                if (result === null || result === undefined) {
                  resultContent = "";
                } else if (typeof result === "string") {
                  resultContent = result;
                } else {
                  try {
                    resultContent = JSON.stringify(result, null, 2);
                  } catch (stringifyError) {
                    resultContent = String(result);
                  }
                }

                if (!toolCall.id) {
                  toolCall.id = `tc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                }

                const toolMessage =
                  modelProvider === "anthropic"
                    ? {
                        role: "tool",
                        tool_call_id: toolCall.id,
                        content: resultContent,
                      }
                    : {
                        role: "tool",
                        tool_call_id: toolCall.id,
                        content: resultContent,
                      };

                return toolMessage;
              });

          if (threadStore) {
            const setStoreState =
              typeof threadStore === "function"
                ? threadStore
                : threadStore.setState;

            setStoreState({
              isExecutingTools: false,
              toolExecutionActive: false,
              isProcessingToolResults: true,
              toolResults,
              // Reset tool call state after follow-up processing
              currentToolCallName: null,
              currentToolCallId: null,
              currentToolCallOutput: null,
            });
          }

          if (actualProviderName === "anthropic") {
            const systemInstruction =
              "Based on the tool results, provide a helpful response to the user's question without making additional tool calls.";
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + systemInstruction;
            } else {
              enhancedInstructions = systemInstruction;
            }
          } else if (actualProviderName === "deepseek") {
            const userInstruction = {
              role: "user",
              content:
                "Please process the above tool results and respond directly to my question without making additional tool calls.",
            };

            toolMessages.push(userInstruction);
          } else if (
            actualProviderName === "google" ||
            actualProviderName === "gemini"
          ) {
            if (isReasoningModel(modelName)) {
              chatParams.reasoning_effort = "high";
            }

            const geminiInstruction =
              "Process the tool results and provide a complete answer. Do not call additional tools.";
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + geminiInstruction;
            } else {
              enhancedInstructions = geminiInstruction;
            }
          }

          const completeToolCalls = JSON.parse(JSON.stringify(validToolCalls));

          const followUpMessages = [];

          const systemMessages = messages.filter((m) => m.role === "system");
          if (systemMessages.length > 0) {
            followUpMessages.push(...systemMessages);
          }

          const userMessages = messages.filter((m) => m.role === "user");
          const lastUserMessage =
            userMessages.length > 0
              ? userMessages[userMessages.length - 1]
              : null;

          if (lastUserMessage) {
            followUpMessages.push(lastUserMessage);
          } else
            for (const toolCall of completeToolCalls) {
              // Don't normalize Anthropic tool call IDs - they start with "toolu_"
              if (modelProvider === "anthropic") {
                // Keep Anthropic IDs as-is
                if (!toolCall.id) {
                  toolCall.id = `toolu_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
                }
              } else if (!toolCall.id || !toolCall.id.startsWith("call_")) {
                const oldId = toolCall.id;
                toolCall.id = `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
              }
            }

          for (const result of toolResults) {
            if (result.toolCall) {
              const matchingToolCall = completeToolCalls.find(
                (tc) => tc.function?.name === result.toolCall.function?.name
              );

              if (matchingToolCall) {
                result.toolCall.id = matchingToolCall.id;
              } else if (modelProvider === "anthropic") {
                // Keep Anthropic tool call IDs
                if (!result.toolCall.id) {
                  result.toolCall.id = `toolu_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
                }
              } else if (
                !result.toolCall.id ||
                !result.toolCall.id.startsWith("call_")
              ) {
                result.toolCall.id = `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
              }
            }
          }

          const assistantMessage = {
            role: "assistant",
            content: null,
            tool_calls: completeToolCalls.map((toolCall) => {
              return {
                id: toolCall.id,
                type: toolCall.type || "function",
                function: {
                  name: toolCall.function?.name || "",
                  arguments: toolCall.function?.arguments || "{}",
                },
              };
            }),
          };

          if (
            !assistantMessage.tool_calls ||
            assistantMessage.tool_calls.length === 0
          ) {
            assistantMessage.tool_calls = [
              {
                id: `call_fallback_${Date.now()}`,
                type: "function",
                function: {
                  name: "fallback_function",
                  arguments: "{}",
                },
              },
            ];
          }

          followUpMessages.push(assistantMessage);
          for (const result of toolResults) {
            const assistantHasToolCallId = assistantMessage.tool_calls.some(
              (tc) => tc.id === result.toolCall.id
            );

            if (!assistantHasToolCallId) {
              assistantMessage.tool_calls.push({
                id: result.toolCall.id,
                type: "function",
                function: {
                  name: result.toolCall.function?.name || "unknown_function",
                  arguments: "{}",
                },
              });
            }

            const toolMessage = {
              role: "tool",
              tool_call_id: result.toolCall.id,
              content:
                typeof result.result === "string"
                  ? result.result
                  : JSON.stringify(result.result),
            };

            followUpMessages.push(toolMessage);
          }

          followUpMessages
            .filter((m) => m.role === "tool")
            .forEach((toolMsg, idx) => {});

          // Check if we have canvas tool results and add specific feedback instructions
          const hasCanvasToolResults = toolResults.some(
            (result) =>
              result.toolCall?.function?.name === "canvas_create" ||
              result.toolCall?.function?.name === "canvas_edit" ||
              result.toolCall?.function?.name === "canvas_stream"
          );

          if (hasCanvasToolResults) {
            const canvasFeedbackInstruction = `
🎨 CRITICAL: CANVAS TOOL FEEDBACK MODE
You have successfully created/edited canvas content. The content is already in the canvas and visible to the user.

YOUR RESPONSE MUST BE:
- A brief, friendly feedback message ONLY
- DO NOT regenerate or summarize the canvas content
- DO NOT write out the content again
- Keep your response to 1-2 sentences maximum

Example responses:
- "I've created the content in the canvas. Let me know if you'd like to make any changes or adjustments."
- "The canvas has been updated with your requested content. Feel free to ask for modifications."
- "I've generated that for you in the canvas. Would you like me to refine or expand on anything?"

REMEMBER: The user can already see the content in the canvas. Just provide a brief acknowledgment.
`;
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + canvasFeedbackInstruction;
            } else {
              enhancedInstructions = canvasFeedbackInstruction;
            }
          }

          // Check if we have web search results and add specific analysis instructions
          const hasWebSearchResults = toolResults.some(
            (result) =>
              result.toolCall?.function?.name === "web_search" ||
              (result.result?.data && Array.isArray(result.result.data)) ||
              (result.result?.sources && Array.isArray(result.result.sources))
          );

          if (hasWebSearchResults) {
            const webSearchAnalysisInstruction = `
🚨 CRITICAL: WEB SEARCH ANALYSIS MODE ACTIVATED
You have received web search tool results. You MUST now create a comprehensive analytical response following this exact format:

1. Start with a direct answer to the user's query
2. Use descriptive section headers (## format)
3. Include specific facts, numbers, and details from the search results
4. Provide inline citations [source name](url) from the sources provided
5. End with key takeaways

NEVER return raw search data. ALWAYS analyze and synthesize the information into a well-structured response like the example format provided in your system instructions.
`;
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + webSearchAnalysisInstruction;
            } else {
              enhancedInstructions = webSearchAnalysisInstruction;
            }
          }

          // Add provider-specific instructions before creating follow-up params
          if (actualProviderName === "anthropic") {
            const systemInstruction =
              "Based on the tool results, provide a helpful response to the user's question without making additional tool calls.";
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + systemInstruction;
            } else {
              enhancedInstructions = systemInstruction;
            }
          } else if (actualProviderName === "deepseek") {
            const userInstruction = {
              role: "user",
              content:
                "Please process the above tool results and respond directly to my question without making additional tool calls.",
            };
            followUpMessages.push(userInstruction);
          } else if (
            actualProviderName === "google" ||
            actualProviderName === "gemini"
          ) {
            if (isReasoningModel(modelName)) {
              chatParams.reasoning_effort = "high";
            }
            const geminiInstruction =
              "Process the tool results and provide a complete answer. Do not call additional tools.";
            if (enhancedInstructions) {
              enhancedInstructions += "\n\n" + geminiInstruction;
            } else {
              enhancedInstructions = geminiInstruction;
            }
          }

          followUpParams = createFollowUpParams({
            modelProvider,
            modelName,
            mutableFollowUpMessages: followUpMessages,
            toolResults,
            enhancedInstructions,
            contextData,
            providerFactory: null, // Backend provider system doesn't need factory
            actualProviderName,
            assistantMessage,
            userMessages,
            generateUUID,
          });

          // Generate follow-up content ID before starting the stream
          followUpContentId = generateUUID();

          // Create initial follow-up message for streaming updates
          if (updateMessageState) {
            updateMessageState({
              id: followUpContentId,
              role: "assistant",
              content: "",
              isStreaming: true,
              messageStatus: "streaming",
              skipDatabaseSave: true,
              // CRITICAL FIX: Include provider and model metadata from the start
              metadata: {
                provider: actualProviderName || modelProvider,
                model: modelName,
                isFollowUpContent: true,
                toolCallMessageId: currentMessageId,
                hasContentInFollowUp: true,
              },
            });
          }

          const followUpStream = await provider
            .createChatCompletionStream(
              followUpParams,
              async (followUpChunk) => {
                try {
                  const chunkResult = processFollowUpChunk({
                    followUpChunk,
                    modelProvider,
                    followUpBuffer,
                    messageBuffer,
                    updateMessageState,
                    currentMessageId,
                    followUpMessageId: followUpContentId, // Pass the follow-up message ID
                    threadStore,
                    threadId: lockedThreadId, // RACE CONDITION FIX: Pass locked thread ID
                    actualProviderName, // CRITICAL FIX: Pass provider name for correct icon
                    modelName, // CRITICAL FIX: Pass model name for correct icon
                  });

                  followUpBuffer = chunkResult.followUpBuffer;
                } catch (error) {
                  console.error(
                    "Error processing follow-up stream chunk:",
                    error
                  );
                }
              },
              (error) => {
                handleFollowUpStreamError({
                  error,
                  updateMessageState,
                  currentMessageId,
                  followUpMessageId: followUpContentId, // Pass the follow-up message ID
                  messageBuffer,
                  followUpBuffer,
                  threadStore,
                  threadId: lockedThreadId, // RACE CONDITION FIX: Pass locked thread ID
                });
              }
            )
            .catch((streamError) => {
              handleFollowUpStreamCreationError({
                streamError,
                updateMessageState,
                currentMessageId,
                followUpMessageId: followUpContentId, // Pass the follow-up message ID
                messageBuffer,
                threadStore,
                toolResults,
                threadId: lockedThreadId, // RACE CONDITION FIX: Pass locked thread ID
              });
            });

          if (updateMessageState) {
            const finalContent =
              messageBuffer + (followUpBuffer ? "\n\n" + followUpBuffer : "");

            const followUpToolCalls = validToolCalls.followUpToolCalls || [];
            const hasFollowUpToolCalls = followUpToolCalls.length > 0;

            const finalToolCalls = hasFollowUpToolCalls
              ? [...validToolCalls, ...followUpToolCalls]
              : validToolCalls;

            const toolOutputsMap = {};
            toolResults.forEach((result) => {
              if (result.toolCall && result.toolCall.id) {
                toolOutputsMap[result.toolCall.id] = result.result;
              }
            });

            if (!followUpMessageSaved) {
              // Construct proper message object for saveToolCallMessage
              const toolCallMessage = {
                id: currentMessageId,
                message_id: currentMessageId,
                thread_id: lockedThreadId,
                workspace_id: workspace_id,
                role: "assistant",
                content: " ", // Tool call messages have minimal content
                tool_calls: finalToolCalls,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                provider: modelProvider,
                model: modelName,
                metadata: {
                  model: modelName,
                  provider: modelProvider,
                  isToolCallMessage: true,
                  timestamp: new Date().toISOString(),
                },
              };

              await saveToolCallMessage(toolCallMessage);
            }

            // ADDED: Update the tool call message with results after tool execution
            if (finalToolCalls.length > 0 && toolResults.length > 0) {
              try {
                // Add results to tool calls
                const toolCallsWithResults = finalToolCalls.map((toolCall) => {
                  const matchingResult = toolResults.find(
                    (result) =>
                      result.toolCall && result.toolCall.id === toolCall.id
                  );

                  if (matchingResult) {
                    // Log image generation results
                    if (toolCall.function?.name === "image_gen") {
                      console.log(
                        "🖼️ [Stream] Adding result to image_gen tool call:",
                        {
                          toolCallId: toolCall.id,
                          hasResult: !!matchingResult.result,
                          resultKeys: matchingResult.result
                            ? Object.keys(matchingResult.result)
                            : [],
                          resultSuccess: matchingResult.result?.success,
                          hasResultResult: !!matchingResult.result?.result,
                          hasResultData: !!matchingResult.result?.result?.data,
                          hasUrl: !!matchingResult.result?.result?.data?.url,
                        }
                      );
                    }

                    return {
                      ...toolCall,
                      result: matchingResult.result,
                    };
                  }
                  return toolCall;
                });

                // Update the tool call message via updateMessageState to trigger ChatContext save
                if (updateMessageState) {
                  console.log(
                    "💾 [Stream] Updating message state with tool results:",
                    {
                      messageId: currentMessageId,
                      toolCallsCount: toolCallsWithResults.length,
                      imageGenCalls: toolCallsWithResults.filter(
                        (tc) => tc.function?.name === "image_gen"
                      ).length,
                    }
                  );

                  updateMessageState({
                    id: currentMessageId,
                    role: "assistant",
                    content: " ", // Keep minimal content - full content goes in separate follow-up message
                    thread_id: lockedThreadId,
                    isStreaming: false,
                    isStreamComplete: true,
                    messageStatus: "completed",
                    skipDatabaseSave: false, // CRITICAL: Enable database save
                    tool_calls: toolCallsWithResults,
                    tools_count: toolCallsWithResults.length,
                    metadata: {
                      model: modelName,
                      provider: modelProvider,
                      timestamp: new Date().toISOString(),
                      message_type: "tool_call_with_results",
                      save_priority: "critical",
                      save_sequence: 1,
                      toolSaveAttempt: "immediate",
                      toolResultsCount: toolResults.length,
                      isToolCallMessage: true,
                      hasContentInFollowUp: true,
                      hasContentInToolCall: true,
                      toolExecutionComplete: true,
                    },
                  });
                }
              } catch (error) {
                console.error(
                  "❌ Error updating tool call message with results:",
                  error
                );
              }
            }
          }
        } catch (toolExecError) {
          if (threadStore) {
            const setStoreState =
              typeof threadStore === "function"
                ? threadStore
                : threadStore.setState;

            setStoreState({
              error: toolExecError.message,
              isStreaming: false,
              streamStatus: "failed",
              toolExecutionActive: false,
              isToolCallStreaming: false,
              isProcessingToolResults: false,
              isExecutingTools: false,
            });
          }
        }
      }

      if (updateMessageState) {
        if (
          modelProvider === "anthropic" &&
          hasDetectedToolCall &&
          pendingToolCalls.length > 0
        ) {
          if (validToolCalls.length === 0) {
            validToolCalls = [...pendingToolCalls];
          }

          updateMessageState({
            id: currentMessageId,
            role: "assistant",
            content: messageBuffer,
            isStreaming: false,
            isStreamComplete: true,
            messageStatus: "completed",
            tool_calls: validToolCalls,
            // Include reasoning content in metadata for DeepSeek reasoner models
            // CRITICAL FIX: Always include provider and model metadata for correct icon display
            metadata: {
              provider: actualProviderName || modelProvider,
              model: modelName,
              // Production build approach: Include reasoning when isReasoningResponse is true
              ...(isReasoningResponse
                ? {
                    reasoningContent: reasoningContent || "",
                    isReasoningResponse: true,
                  }
                : {}),
              ...(reasoningMetadata || {}),
              // Tool selection context for debugging
              toolSelectionContext,
              // System prompt (only in dev mode with env var)
              ...(import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG === "true" &&
              enhancedInstructions
                ? { systemPrompt: enhancedInstructions }
                : {}),
            },
            thread_id: lockedThreadId, // RACE CONDITION FIX: Include thread ID
          });

          // Create proper updateState function for tool handling
          const updateState = (stateUpdate) => {
            if (threadStore) {
              const setStoreState =
                typeof threadStore === "function"
                  ? threadStore
                  : threadStore.setState;
              setStoreState(stateUpdate);
            }
          };

          handleToolCalls(
            {
              choices: [
                {
                  message: {
                    tool_calls: validToolCalls,
                  },
                },
              ],
            },
            updateState,
            toolRegistry,
            modelProvider
          );
        } else {
          if (
            typeof saveToDatabase === "function" &&
            lockedThreadId &&
            !hasDetectedToolCall
          ) {
            try {
              const assistantMessage = {
                id: currentMessageId,
                message_id: currentMessageId,
                thread_id: lockedThreadId,
                workspace_id: workspace_id,
                role: "assistant",
                content: messageBuffer || " ",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                provider: actualProviderName || modelProvider,
                provider_message_id: currentMessageId,
                metadata: {
                  provider: actualProviderName,
                  model: modelName,
                  timestamp: new Date().toISOString(),
                  isComplete: true,
                  // Production build approach: Include reasoning when isReasoningResponse is true
                  ...(isReasoningResponse
                    ? {
                        reasoningContent: reasoningContent || "",
                        isReasoningResponse: true,
                        // FIXED: Add reasoning duration fields for persistence
                        ...(reasoningDuration > 0
                          ? {
                              reasoning_seconds: reasoningDuration,
                              reasoningSeconds: reasoningDuration,
                              reasoningDuration: reasoningDuration,
                            }
                          : {}),
                      }
                    : {}),
                  ...(reasoningMetadata || {}),
                  // Include citations if present (for Perplexity responses)
                  ...(reasoningMetadata?.citations
                    ? {
                        citations: reasoningMetadata.citations,
                        hasCitations: true,
                      }
                    : {}),
                  // Tool selection context for debugging
                  toolSelectionContext,
                  // System prompt (only in dev mode with env var)
                  ...(import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG ===
                    "true" && enhancedInstructions
                    ? { systemPrompt: enhancedInstructions }
                    : {}),
                },
                messageStatus: "completed",
                isStreamComplete: true,
                isStreaming: false,
              };

              console.log(
                "[StreamProviderChat] Saving assistant message with metadata:",
                {
                  messageId: assistantMessage.id,
                  hasCitations: !!assistantMessage.metadata?.citations,
                  citationsCount:
                    assistantMessage.metadata?.citations?.length || 0,
                  hasReasoning: !!assistantMessage.metadata?.reasoningContent,
                  reasoningDuration:
                    assistantMessage.metadata?.reasoningDuration ||
                    assistantMessage.metadata?.reasoning_seconds,
                  metadataKeys: Object.keys(assistantMessage.metadata || {}),
                  hasToolSelectionContext:
                    !!assistantMessage.metadata?.toolSelectionContext,
                  hasSystemPrompt: !!assistantMessage.metadata?.systemPrompt,
                  toolSelectionContext:
                    assistantMessage.metadata?.toolSelectionContext,
                  showSystemPromptDebug:
                    import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG === "true",
                  envVarValue: import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG,
                }
              );
              await saveToDatabase(assistantMessage);

              // CRITICAL FIX: Also update the message in thread store so UI shows citations immediately
              if (threadStore && updateMessageState) {
                updateMessageState({
                  id: currentMessageId,
                  metadata: assistantMessage.metadata,
                  isStreamComplete: true,
                  isStreaming: false,
                  messageStatus: "completed",
                });
                console.log(
                  "[StreamProviderChat] Updated message in thread store with citations"
                );
              }
            } catch (saveError) {
              console.error(
                "Error saving regular assistant message:",
                saveError
              );
            }
          }
        }
      }

      // Tool execution completed

      if (threadStore) {
        const finalCombinedContent =
          followUpBuffer && followUpBuffer.length > 0
            ? messageBuffer + "\n\n" + followUpBuffer
            : messageBuffer;

        const followUpToolCalls = validToolCalls.followUpToolCalls || [];
        const hasFollowUpToolCalls = followUpToolCalls.length > 0;
        const finalToolCalls = hasFollowUpToolCalls
          ? [...validToolCalls, ...followUpToolCalls]
          : validToolCalls;

        const finalMetadata = {
          timestamp: new Date().toISOString(),
          model: modelName,
          // CRITICAL FIX: Use actualProviderName instead of modelProvider for correct icon
          provider: actualProviderName || modelProvider,
          has_tool_calls: finalToolCalls && finalToolCalls.length > 0,

          followUpCompleted: true,
          toolResults: toolResults || [],
          // Production build approach: Include reasoning when isReasoningResponse is true
          ...(isReasoningResponse
            ? {
                reasoningContent: reasoningContent || "",
                isReasoningResponse: true,
                // FIXED: Add reasoning duration to metadata so it shows in UI
                ...(reasoningDuration > 0
                  ? {
                      reasoning_seconds: reasoningDuration,
                      reasoningSeconds: reasoningDuration,
                      reasoningDuration: reasoningDuration,
                    }
                  : {}),
              }
            : {}),
          // Include reasoning metadata if available (includes citations for Perplexity)
          ...(reasoningMetadata || {}),
          // Tool selection context for debugging
          toolSelectionContext,
          // System prompt (only in dev mode with env var)
          ...(() => {
            const shouldInclude =
              import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG === "true";
            if (import.meta.env.DEV) {
              console.log(
                "[StreamProviderChat] System prompt debug check in finalMetadata:",
                {
                  envVarValue: import.meta.env.VITE_SHOW_SYSTEM_PROMPT_DEBUG,
                  shouldInclude,
                  hasInstructions: !!enhancedInstructions,
                  instructionsLength: enhancedInstructions?.length || 0,
                }
              );
            }
            return shouldInclude && enhancedInstructions
              ? { systemPrompt: enhancedInstructions }
              : {};
          })(),
          // Ensure citations are included if present
          ...(reasoningMetadata?.citations
            ? {
                citations: reasoningMetadata.citations,
                hasCitations: true,
              }
            : {}),
          ...(contextData || {}),
        };

        // Debug log for final metadata
        if (reasoningMetadata?.citations) {
          console.log(
            "[StreamProviderChat] Final metadata includes citations:",
            {
              citationsCount: reasoningMetadata.citations.length,
              provider: actualProviderName || modelProvider,
            }
          );
        }

        // Anthropic interleaving logic removed for simplification

        // Get current messages before updating thread state
        let currentState;
        if (typeof threadStore === "function" && !threadStore.getState) {
          // threadStore is actually setState, get state from the store directly
          currentState = useThreadStore.getState();
        } else if (typeof threadStore?.getState === "function") {
          currentState = threadStore.getState();
        } else {
          currentState = {};
        }

        const currentMessages = currentState.messages || [];

        let updatedMessages = currentMessages;

        if (Array.isArray(currentMessages) && currentMessages.length > 0) {
          updatedMessages = currentMessages.map((msg) => {
            // Update the tool call message (not the follow-up message)
            if (msg.id === currentMessageId) {
              return {
                ...msg,
                content: msg.content || " ", // Tool call message keeps minimal content
                isStreaming: false,
                isStreamComplete: true,
                messageStatus: "completed",
                // CRITICAL FIX: Merge metadata to preserve existing provider/model info
                // Preserve toolSelectionContext and systemPrompt from original metadata if they exist
                metadata: {
                  ...(msg.metadata || {}),
                  ...finalMetadata,
                  // Ensure provider and model are always set
                  provider:
                    actualProviderName ||
                    modelProvider ||
                    msg.metadata?.provider,
                  model: modelName || msg.metadata?.model,
                  // Preserve toolSelectionContext and systemPrompt if they were in original metadata
                  ...(msg.metadata?.toolSelectionContext
                    ? {
                        toolSelectionContext: msg.metadata.toolSelectionContext,
                      }
                    : {}),
                  ...(msg.metadata?.systemPrompt
                    ? { systemPrompt: msg.metadata.systemPrompt }
                    : {}),
                },
                tool_calls:
                  finalToolCalls && finalToolCalls.length > 0
                    ? finalToolCalls
                    : msg.tool_calls,
              };
            }
            // Update follow-up messages to ensure they're marked as complete
            else if (
              msg.metadata?.isFollowUpContent &&
              msg.metadata?.toolCallMessageId === currentMessageId
            ) {
              return {
                ...msg,
                isStreaming: false,
                isStreamComplete: true,
                messageStatus: "completed",
                // CRITICAL FIX: Preserve and ensure provider metadata is set
                metadata: {
                  ...(msg.metadata || {}),
                  provider:
                    actualProviderName ||
                    modelProvider ||
                    msg.metadata?.provider,
                  model: modelName || msg.metadata?.model,
                },
              };
            }
            return msg;
          });

          const hasAssistantMessage = updatedMessages.some(
            (msg) => msg.id === currentMessageId
          );

          // Only add tool call message if it doesn't exist (shouldn't happen)
          if (
            !hasAssistantMessage &&
            finalToolCalls &&
            finalToolCalls.length > 0
          ) {
            updatedMessages.push({
              id: currentMessageId,
              role: "assistant",
              content: " ", // Minimal content for tool call message
              isStreaming: false,
              isStreamComplete: true,
              messageStatus: "completed",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              thread_id: lockedThreadId,
              workspace_id: workspace_id,
              metadata: finalMetadata || {},
              tool_calls: finalToolCalls,
              tools_count: finalToolCalls.length,
            });
          }
        }

        // Update thread state with both global state and updated messages in one call
        const streamCompleteUpdates = {
          isStreaming: false,
          streamStatus: "completed",
          messageStatus: "completed",
          isStreamComplete: true,
          abortController: null,
          toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
          tool_calls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: false,
          isLoading: false,
          error: null,
          preventNewMessageClear: true,
          preserveMetadata: false, // Don't preserve metadata to ensure streaming state updates properly
          finalMetadata: finalMetadata,
          // Reset tool call state after stream completion
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
          // CRITICAL FIX: Clear currentMessageId when streaming completes
          currentMessageId: null,
          // Include updated messages to ensure assistant message isStreaming is set to false
          messages: updatedMessages,
          // CRITICAL FIX: Add timestamp to track when streaming completed
          lastStreamCompletionTime: Date.now(),
          streamCompletedAt: new Date().toISOString(),
        };

        safelyUpdateThreadState(threadStore, streamCompleteUpdates);

        // BACKGROUND STREAMING: Ensure final message state is in threadMessages Map
        // This is critical for background streams where the user might navigate to the thread later
        if (threadIdForStream && currentMessageId && updateMessageState) {
          // Find the completed assistant message
          const completedMessage = updatedMessages.find(
            (msg) => msg.id === currentMessageId && msg.role === "assistant"
          );

          if (completedMessage) {
            // Force update the threadMessages Map with the complete message
            updateMessageState({
              ...completedMessage,
              isStreaming: false,
              isStreamComplete: true,
              messageStatus: "completed",
              thread_id: threadIdForStream,
              skipDatabaseSave: true, // Already handled by stream completion
            });
          }
        }

        // BACKGROUND STREAMING: Unregister stream on completion
        if (threadIdForStream && messageIdForStream) {
          const threadStoreState = useThreadStore.getState();
          threadStoreState.unregisterStream(
            threadIdForStream,
            messageIdForStream
          );
        }

        // AUTO-ANALYSIS: Extract and save important information from conversations
        // This runs in the background (non-blocking) after stream completion
        // Rate limited: 15 second cooldown per user (client-side + server-side)
        // First analysis in a thread runs immediately (no cooldown)
        // DISABLED: Memory system not ready for production
        const MEMORY_SYSTEM_ENABLED = false;
        if (MEMORY_SYSTEM_ENABLED && userId) {
          try {
            const authState = useAuthStore.getState();
            const userSettings = authState.profile?.settings?.personalization;

            if (userSettings?.auto_extract_memories && canRunAutoAnalysis(userId, lockedThreadId)) {
              const isFirstAnalysis = !firstAnalysisRun.has(lockedThreadId);
              console.log(
                `[Memory] Auto-analysis checking for new messages${isFirstAnalysis ? " (first in thread)" : ""}...`
              );

              // Only analyze the most recent user-assistant exchange (not all messages)
              // This prevents re-analyzing messages that were already processed
              // Filter to get only the latest user message and the current assistant response
              const unanalyzedMessages = messages
                .slice(-4) // Look at last 4 messages max for context
                .filter((m) => {
                  // Skip messages that have already been analyzed for memories
                  if (m.metadata?.extractedMemories?.length > 0 || m.metadata?.memoryExtractionTime) {
                    return false;
                  }
                  return true;
                })
                .slice(-2); // Only keep the last 2 unanalyzed messages (user + assistant)

              const recentMessages = unanalyzedMessages.map((m) => ({
                role: m.role,
                content:
                  typeof m.content === "string"
                    ? m.content
                    : m.content?.[0]?.text || "",
              }));

              // Skip if no new messages to analyze
              if (recentMessages.length === 0) {
                console.log("[Memory] No new messages to analyze (all already processed)");
                return;
              }

              console.log(`[Memory] Analyzing ${recentMessages.length} new message(s)`);

              // Record the attempt before making the call
              recordAutoAnalysis(userId, lockedThreadId);

              // Call analyze in background (non-blocking)
              // Capture the message ID for use in the callback
              const targetMessageId = currentMessageId;
              memoryService
                .analyzeMessages(userId, recentMessages, {
                  workspaceId: workspace_id,
                  threadId: lockedThreadId,
                  context: `Thread: ${lockedThreadId}`,
                })
                .then((result) => {
                  if (result?.memories?.length > 0) {
                    console.log(
                      `[Memory] Auto-analysis saved ${result.memories.length} memory/memories from conversation`
                    );

                    // Update message metadata with extracted memories for UI indicator
                    if (lockedThreadId && targetMessageId) {
                      const threadStoreState = useThreadStore.getState();

                      // Use analysis.memories for content (has actual text from OpenAI analysis)
                      // The result.memories from Mem0 v2 API may only have pending/queued status
                      const analysisMemories = result.analysis?.memories || [];

                      // Debug: Log memory structure
                      console.log("[Memory] Analysis memories:", JSON.stringify(analysisMemories, null, 2));

                      // Extract memories from the analysis result (which has the actual content)
                      const extractedMemories = analysisMemories.map((m, index) => {
                        return {
                          id: result.memories[index]?.id || result.memories[index]?.event_id || `mem-${index}`,
                          content: m.content || m.memory || "",
                          category: m.category || "general",
                          importance: m.importance || "medium",
                        };
                      });

                      threadStoreState.updateMessageMetadata(
                        lockedThreadId,
                        targetMessageId,
                        {
                          extractedMemories,
                          memoryExtractionTime: new Date().toISOString(),
                        }
                      );

                      // Dispatch event for toast notification (high-importance memories)
                      const hasHighImportance = analysisMemories.some(
                        (m) => m.importance === "high"
                      );
                      if (hasHighImportance) {
                        window.dispatchEvent(
                          new CustomEvent("memory-extracted", {
                            detail: {
                              count: extractedMemories.length,
                              memories: extractedMemories,
                              messageId: targetMessageId,
                              threadId: lockedThreadId,
                            },
                          })
                        );
                      }

                      // Dispatch event to update memory counts on project pages
                      window.dispatchEvent(
                        new CustomEvent("memory-count-updated", {
                          detail: {
                            workspaceId: workspace_id,
                            action: "add",
                            count: extractedMemories.length,
                          },
                        })
                      );
                    }
                  } else {
                    console.log(`[Memory] Auto-analysis complete - no new memories extracted`);
                  }
                })
                .catch((err) => {
                  // Handle rate limit errors gracefully
                  if (err?.status === 429 || err?.message?.includes("Rate limited")) {
                    console.debug("[Memory] Auto-analysis rate limited by server, will retry later");
                  } else {
                    console.error("[Memory] Auto-analysis failed:", err);
                  }
                });
            } else if (userSettings?.auto_extract_memories) {
              console.log("[Memory] Auto-analysis skipped - cooldown active");
            } else {
              console.log("[Memory] Auto-analysis disabled in settings");
            }
          } catch (autoAnalysisError) {
            // Don't fail the stream completion if auto-analysis fails
            console.error(
              "[Memory] Error checking auto-analysis settings:",
              autoAnalysisError
            );
          }
        }

        // UPDATED: Instead of saving directly, update the follow-up message to trigger ChatContext save
        if (
          hasDetectedToolCall &&
          (validToolCalls.length > 0 || pendingToolCalls.length > 0) &&
          finalCombinedContent &&
          finalCombinedContent.trim() !== ""
        ) {
          try {
            // SIMPLIFIED: Use the followUpContentId directly instead of searching thread store
            // We know the ID from when we created the follow-up message earlier

            // CRITICAL FIX: Validate message ID before updating to prevent role corruption
            if (updateMessageState && followUpContentId) {
              // Validate that followUpContentId is not a user message ID
              const currentState =
                typeof threadStore.getState === "function"
                  ? threadStore.getState()
                  : {};
              const existingMessage = currentState.messages?.find(
                (m) => m.id === followUpContentId
              );

              if (existingMessage && existingMessage.role === "user") {
                console.error(
                  "🚨 CRITICAL ERROR: followUpContentId points to a user message! This would corrupt the user message."
                );
                console.error("Existing message:", existingMessage);
                console.error("Attempted update role: assistant");
                return; // Abort the update to prevent corruption
              }

              // REMOVED: Problematic setTimeout delay that was causing race conditions
              // Instead, use immediate update with proper save sequencing via priority system
              updateMessageState({
                id: followUpContentId,
                role: "assistant",
                content: finalCombinedContent,
                thread_id: lockedThreadId,
                isStreaming: false,
                isStreamComplete: true,
                messageStatus: "completed",
                skipDatabaseSave: false, // CRITICAL: Enable database save
                // REMOVED: Future timestamps that were causing ordering issues
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                metadata: {
                  isFollowUpContent: true,
                  toolCallMessageId: currentMessageId,
                  followUpCompleted: true,
                  completedAt: new Date().toISOString(),
                  hasContentInFollowUp: true,
                  // FIXED: Use proper save priority instead of setTimeout delays
                  save_priority: "low",
                  save_sequence: 3, // After tool call message (sequence 1)
                },
              });
            } else {
              console.warn(
                "⚠️ Cannot update follow-up message: missing updateMessageState or followUpContentId",
                {
                  hasUpdateMessageState: !!updateMessageState,
                  followUpContentId: followUpContentId,
                }
              );
            }
          } catch (error) {
            console.error(
              "❌ Error updating follow-up message for ChatContext save:",
              error
            );
          }
        }
      }

      // Record token usage for all providers
      if (tokenCounter && tokenCounter.totalTokens > 0) {
        try {
          // Get userId from various sources
          const recordUserId =
            userId || contextData?.userId || contextData?.user_id;

          if (recordUserId) {
            await tokenCounter.recordTokenUsage(
              recordUserId,
              workspace_id,
              modelName,
              "chat_completions"
            );
          } else {
            console.error("Cannot record token usage: Missing userId");
          }
        } catch (tokenError) {
          console.error(
            "Failed to record token usage for error case:",
            tokenError
          );
        }
      }

      return {
        messageId: currentMessageId,
        content: messageBuffer,
        isComplete: true,
        functionCalls: pendingToolCalls,
        threadId: lockedThreadId,
        provider: modelProvider,
        provider_message_id: currentMessageId,
      };
    } catch (error) {
      if (error.name === "AbortError" || error.message?.includes("abort")) {
        if (threadStore) {
          const setThreadState =
            typeof threadStore === "function"
              ? threadStore
              : threadStore.setState;

          setThreadState({
            isStreaming: false,
            streamStatus: "aborted",
            messageStatus: "aborted",
            isStreamComplete: true,
            isToolCallStreaming: false,
            toolExecutionActive: false,
            abortController: null,
            error: null,
            // Reset tool call state on abort
            currentToolCallName: null,
            currentToolCallId: null,
            currentToolCallOutput: null,
            // CRITICAL FIX: Clear currentMessageId on abort
            currentMessageId: null,
          });
        }

        if (updateMessageState && messageId) {
          updateMessageState({
            id: messageId,
            isStreaming: false,
            messageStatus: "aborted",
            content: contextData?.preserveContentOnAbort
              ? messageBuffer
              : messageBuffer
                ? messageBuffer + "\n\n🛑 Response stopped by user"
                : "🛑 Response stopped by user",
          });
        }

        // BACKGROUND STREAMING: Unregister stream on abort
        if (threadIdForStream && messageIdForStream) {
          const threadStoreState = useThreadStore.getState();
          threadStoreState.unregisterStream(
            threadIdForStream,
            messageIdForStream
          );
        }
        return;
      }

      // BACKGROUND STREAMING: Unregister stream on error
      if (threadIdForStream && messageIdForStream) {
        const threadStoreState = useThreadStore.getState();
        threadStoreState.unregisterStream(
          threadIdForStream,
          messageIdForStream
        );
      }

      let errorMessage = error.message;
      const isProviderError = errorMessage.includes(`API Error`);

      if (isProviderError) {
        errorMessage = `${modelProvider} provider error: ${errorMessage}`;
      }

      // Compute user-friendly message once and reuse it
      const friendlyErrorMessage = getUserFriendlyErrorMessage(errorMessage);

      // Capture streaming errors in Sentry with context tags
      Sentry.captureException(error, {
        tags: {
          component: "streamProviderChat",
          provider: modelProvider,
          model: modelName,
          errorType: getErrorType(errorMessage),
        },
        extra: {
          threadId: lockedThreadId,
          messageId: currentMessageId,
          errorMessage: errorMessage,
          isStreamError: error.isStreamError ?? false,
        },
      });

      if (typeof saveToDatabase === "function" && lockedThreadId) {
        try {
          const errorMessageData = {
            id: currentMessageId,
            message_id: currentMessageId,
            thread_id: lockedThreadId,
            workspace_id: workspace_id,
            role: "assistant",
            content: friendlyErrorMessage,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            metadata: {
              error: errorMessage,
              isError: true,
              timestamp: new Date().toISOString(),
              provider: modelProvider,
            },
            provider: modelProvider,
            provider_message_id: currentMessageId,
          };

          await saveToDatabase(errorMessageData);
        } catch (dbError) {
          console.error("Failed to save error message to database:", dbError);
        }
      }

      if (threadStore) {
        try {
          let store;
          if (typeof threadStore === "function" && !threadStore.getState) {
            store = useThreadStore.getState();
          } else if (typeof threadStore?.getState === "function") {
            store = threadStore.getState();
          } else {
            store = threadStore;
          }

          if (store.abortController) {
            store.abortController.abort();
          }
        } catch (storeError) {
          console.error("Error accessing thread store:", storeError);
        }
      }

      if (threadStore) {
        const errorState = {
          error: errorMessage,
          isLoading: false,
          isStreaming: false,
          streamStatus: "failed",
          messageStatus: "error",
          isStreamComplete: true,
          abortController: null,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: false,
          preventNewMessageClear: true,
          preserveMetadata: true,
          // CRITICAL FIX: Clear currentMessageId on error
          currentMessageId: null,
        };

        safelyUpdateThreadState(threadStore, errorState);

        if (
          typeof threadStore.getState === "function" &&
          (typeof threadStore.getState().updateMessage === "function"
            ? typeof threadStore.getState().updateMessage === "function"
            : typeof useThreadStore.getState().updateMessage === "function")
        ) {
          const updateMessage =
            typeof threadStore.getState === "function"
              ? threadStore.getState().updateMessage
              : useThreadStore.getState().updateMessage;

          try {
            await updateMessage(currentMessageId, {
              content: friendlyErrorMessage,
              isStreaming: false,
              isStreamComplete: true,
              messageStatus: "error",
              isError: true,
              errorType: getErrorType(errorMessage),
              canRetry: error.retryable ?? isRetryableError(errorMessage),
              updated_at: new Date().toISOString(),
            });
          } catch (updateError) {
            console.error("Failed to update message with error:", updateError);
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Mark error as already handled to prevent outer catch from updating message again
      error.errorMessageAlreadyHandled = true;
      throw error;
    }
  } catch (error) {
    if (error.name === "AbortError" || error.message?.includes("abort")) {
      if (threadStore) {
        const setThreadState =
          typeof threadStore === "function"
            ? threadStore
            : threadStore.setState;

        setThreadState({
          isStreaming: false,
          streamStatus: "aborted",
          messageStatus: "aborted",
          isStreamComplete: true,
          isToolCallStreaming: false,
          toolExecutionActive: false,
          abortController: null,
          error: null,
          // Reset tool call state on abort
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
        });
      }

      if (updateMessageState && messageId) {
        updateMessageState({
          id: messageId,
          isStreaming: false,
          messageStatus: "aborted",
          content: contextData?.preserveContentOnAbort
            ? messageBuffer
            : messageBuffer
              ? messageBuffer + "\n\n🛑 Response stopped by user"
              : "🛑 Response stopped by user",
        });
      }

      // Record token usage even for aborted requests
      if (tokenCounter && tokenCounter.totalTokens > 0) {
        try {
          const recordUserId =
            userId || contextData?.userId || contextData?.user_id;
          if (recordUserId) {
            await tokenCounter.recordTokenUsage(
              recordUserId,
              workspace_id,
              modelName,
              "chat_completions"
            );
          }
        } catch (tokenError) {
          console.error(
            "Failed to record token usage for aborted request:",
            tokenError
          );
        }
      }

      return;
    }

    // Skip message updates if error was already handled by inner catch block
    // This prevents calling getUserFriendlyErrorMessage() twice and showing cycling messages
    if (error.errorMessageAlreadyHandled) {
      // Still need to reset some state, but don't update message content
      if (threadStore) {
        const setStoreState =
          typeof threadStore === "function" ? threadStore : threadStore.setState;

        const currentState =
          typeof threadStore.getState === "function"
            ? threadStore.getState()
            : {};

        setStoreState({
          error: error.message,
          isLoading: false,
          isStreaming: false,
          streamStatus: "failed",
          messageStatus: "error",
          isStreamComplete: true,
          abortController: null,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: false,
          messages: currentState.messages || [],
          preventNewMessageClear: currentState.preventNewMessageClear,
          pendingUserMessage: currentState.pendingUserMessage,
          pendingUserMessageId: currentState.pendingUserMessageId,
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
        });
      }
    } else {
      // Error was not handled by inner catch - handle it here
      let errorMessage = error.message;
      const isProviderError = errorMessage.includes(`API Error`);

      if (isProviderError) {
        errorMessage = `${modelProvider} provider error: ${errorMessage}`;
      }

      // Compute user-friendly message once and reuse it
      const friendlyErrorMessage = getUserFriendlyErrorMessage(errorMessage);

      // Capture streaming errors in Sentry (outer catch block)
      Sentry.captureException(error, {
        tags: {
          component: "streamProviderChat",
          provider: modelProvider,
          model: modelName,
          errorType: getErrorType(errorMessage),
          catchBlock: "outer",
        },
        extra: {
          threadId: lockedThreadId,
          messageId: currentMessageId,
          errorMessage: errorMessage,
          isStreamError: error.isStreamError ?? false,
        },
      });

      if (typeof saveToDatabase === "function" && lockedThreadId) {
        try {
          const errorMessageData = {
            id: currentMessageId,
            message_id: currentMessageId,
            thread_id: lockedThreadId,
            workspace_id: workspace_id,
            role: "assistant",
            content: friendlyErrorMessage,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            metadata: {
              error: errorMessage,
              isError: true,
              timestamp: new Date().toISOString(),
              provider: modelProvider,
            },
            provider: modelProvider,
            provider_message_id: currentMessageId,
          };

          await saveToDatabase(errorMessageData);
        } catch (dbError) {
          console.error("Failed to save error message to database:", dbError);
        }
      }

      if (threadStore) {
        try {
          const store =
            typeof threadStore.getState === "function"
              ? threadStore.getState()
              : threadStore;

          if (store.abortController) {
            store.abortController.abort();
          }
        } catch (storeError) {
          console.error("Error accessing thread store:", storeError);
        }
      }

      if (threadStore) {
        const setStoreState =
          typeof threadStore === "function" ? threadStore : threadStore.setState;

        const currentState =
          typeof threadStore.getState === "function"
            ? threadStore.getState()
            : {};

        setStoreState({
          error: errorMessage,
          isLoading: false,
          isStreaming: false,
          streamStatus: "failed",
          messageStatus: "error",
          isStreamComplete: true,
          abortController: null,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: false,
          messages: currentState.messages || [],
          preventNewMessageClear: currentState.preventNewMessageClear,
          pendingUserMessage: currentState.pendingUserMessage,
          pendingUserMessageId: currentState.pendingUserMessageId,
          // Reset tool call state on error
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
        });

        if (
          typeof threadStore.getState === "function" &&
          (typeof threadStore.getState().updateMessage === "function"
            ? typeof threadStore.getState().updateMessage === "function"
            : typeof useThreadStore.getState().updateMessage === "function")
        ) {
          const updateMessage =
            typeof threadStore.getState === "function"
              ? threadStore.getState().updateMessage
              : useThreadStore.getState().updateMessage;

          try {
            await updateMessage(currentMessageId, {
              content: friendlyErrorMessage,
              isStreaming: false,
              isStreamComplete: true,
              messageStatus: "error",
              isError: true,
              errorType: getErrorType(errorMessage),
              canRetry: error.retryable ?? isRetryableError(errorMessage),
              updated_at: new Date().toISOString(),
            });
          } catch (updateError) {
            console.error("Failed to update message with error:", updateError);
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Record token usage even for error cases
    if (tokenCounter && tokenCounter.totalTokens > 0) {
      try {
        const recordUserId =
          userId || contextData?.userId || contextData?.user_id;
        if (recordUserId) {
          await tokenCounter.recordTokenUsage(
            recordUserId,
            workspace_id,
            modelName,
            "chat_completions"
          );
        }
      } catch (tokenError) {
        console.error(
          "Failed to record token usage for error case:",
          tokenError
        );
      }
    }

    throw error;
  } finally {
    if (threadStore) {
      // Fix: Use safelyUpdateThreadState to handle both store types
      safelyUpdateThreadState(threadStore, {
        abortController: null,
        isStreaming: false,
        isStreamComplete: true,
      });
    }
  }
}

/**
 * Process structured streaming chunk for canvas
 * @param {Object} chunk - The streaming chunk
 * @param {string} streamId - Stream identifier
 * @param {Object} contextData - Context data
 */
function processCanvasStructuredChunk(chunk, streamId, contextData) {
  try {
    if (!chunk || !contextData?.isCanvasModeEnabled) {
      return;
    }

    // Handle structured output format
    if (chunk.blocks && Array.isArray(chunk.blocks)) {
      canvasEventEmitter.emit("structured_stream", {
        streamId: chunk.streamId || streamId,
        isComplete: chunk.isComplete || false,
        contentType: chunk.contentType || "text",
        language: chunk.language,
        blocks: chunk.blocks,
        metadata: {
          ...chunk.metadata,
          timestamp: new Date().toISOString(),
        },
      });
    } else if (chunk.content) {
      // Handle regular content streaming with conversion to blocks

      const blocks = convertContentToBlocks(
        chunk.content,
        chunk.contentType || "text"
      );

      canvasEventEmitter.emit("canvas_stream", {
        content: chunk.content,
        blocks: blocks,
        isComplete: chunk.isComplete || false,
        streamId: chunk.streamId || streamId,
        isStreaming: !chunk.isComplete,
        type: chunk.contentType || "text",
        language: chunk.language,
        metadata: chunk.metadata,
      });
    }
  } catch (error) {
    console.error("Error processing canvas structured chunk:", error);
  }
}

/**
 * Convert content to BlockNote-compatible blocks
 * @param {string} content - Content to convert
 * @param {string} type - Content type
 * @returns {Array} BlockNote blocks
 */
function convertContentToBlocks(content, type = "text") {
  if (!content) return [];

  try {
    if (type === "code") {
      return [
        {
          id: `code_${Date.now()}`,
          type: "codeBlock",
          props: {
            language: "javascript",
            textColor: "default",
            backgroundColor: "default",
            textAlignment: "left",
          },
          content: [content],
          children: [],
        },
      ];
    } else if (type === "markdown") {
      const lines = content.split("\n");
      const blocks = [];

      lines.forEach((line, index) => {
        if (line.startsWith("# ")) {
          blocks.push({
            id: `heading_${Date.now()}_${index}`,
            type: "heading",
            props: {
              level: 1,
              textColor: "default",
              backgroundColor: "default",
              textAlignment: "left",
            },
            content: [line.substring(2)],
            children: [],
          });
        } else if (line.startsWith("## ")) {
          blocks.push({
            id: `heading_${Date.now()}_${index}`,
            type: "heading",
            props: {
              level: 2,
              textColor: "default",
              backgroundColor: "default",
              textAlignment: "left",
            },
            content: [line.substring(3)],
            children: [],
          });
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
          blocks.push({
            id: `bullet_${Date.now()}_${index}`,
            type: "bulletListItem",
            props: {
              textColor: "default",
              backgroundColor: "default",
              textAlignment: "left",
            },
            content: [line.substring(2)],
            children: [],
          });
        } else if (line.trim()) {
          blocks.push({
            id: `paragraph_${Date.now()}_${index}`,
            type: "paragraph",
            props: {
              textColor: "default",
              backgroundColor: "default",
              textAlignment: "left",
            },
            content: [line],
            children: [],
          });
        }
      });

      return blocks;
    } else {
      // Regular text - split into paragraphs
      const paragraphs = content.split("\n\n").filter((p) => p.trim());
      return paragraphs.map((paragraph, index) => ({
        id: `paragraph_${Date.now()}_${index}`,
        type: "paragraph",
        props: {
          textColor: "default",
          backgroundColor: "default",
          textAlignment: "left",
        },
        content: [paragraph.trim()],
        children: [],
      }));
    }
  } catch (error) {
    return [
      {
        id: `error_${Date.now()}`,
        type: "paragraph",
        props: {
          textColor: "red",
          backgroundColor: "default",
          textAlignment: "left",
        },
        content: [`Error: ${error.message}`],
        children: [],
      },
    ];
  }
}

/**
 * Enhanced streaming function that handles structured outputs
 */
export async function streamProviderChatEnhanced(
  providerConfig,
  messages,
  contextData,
  onDelta,
  onComplete,
  onError,
  onToolCall
) {
  try {
    // Generate stream ID for canvas
    const streamId = contextData?.streamId || `stream_${Date.now()}`;
    contextData.streamId = streamId;

    // Start the regular streaming
    const result = await streamProviderChat(
      providerConfig,
      messages,
      contextData,
      (delta) => {
        // Handle regular delta
        if (onDelta) onDelta(delta);

        // Process for canvas if enabled
        if (contextData?.isCanvasModeEnabled) {
          processCanvasStructuredChunk(delta, streamId, contextData);
        }
      },
      (complete) => {
        // Handle completion
        if (onComplete) onComplete(complete);

        // Handle canvas completion
        if (contextData?.isCanvasModeEnabled) {
          processCanvasStructuredChunk(
            { ...complete, isComplete: true },
            streamId,
            contextData
          );
        }
      },
      onError,
      onToolCall
    );

    return result;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
}
